import { useReducedMotion } from "@src/packages/moon-ui/hooks/useReducedMotion";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Animated,
  View,
  Text,
  ScrollView,
  Pressable,
  ActivityIndicator,
  Image,
  StyleSheet,
  Easing,
  useWindowDimensions,
} from "react-native";
import { useTranslations } from "@src/packages/i18n";
import { useLocale } from "@src/packages/i18n/useLocale";
import { formatDate } from "@src/packages/i18n/formatters";
import type { Locale } from "@src/packages/i18n/locale";
import { useSmartBack } from "../hooks/useSmartBack";
import { useCycleNavigation } from "../hooks/useCycleNavigation";
import { usePastCycleDetail } from "@src/packages/cycles/hooks/usePastCycleDetail";
import {
  CANCELLATION_REASON_KEYS,
  type CancellationReason,
} from "@src/packages/cycles/hooks/useCycles";
import {
  ArrowLeft,
  History,
  Plus,
  AlertTriangle,
  Syringe,
  Target,
  Sparkles,
  Beaker,
  Baby,
  Hourglass,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Droplets,
  FileText,
  HeartHandshake,
  Link2,
  Lock,
  Send,
  Snowflake,
  Upload,
  WandSparkles,
  type LucideIcon,
} from "lucide-react-native";
import Svg, {
  Path,
  Defs,
  LinearGradient as SvgLinearGradient,
  Stop,
} from "react-native-svg";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import type { CareStackParamList } from "../types";
import { useDocumentUploadController } from "../DocumentUploadController";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import {
  ActionBanner,
  CardSubtitleText,
  CardTitleText,
  ClinicalDataStatus,
  Dialog,
  DialogHeader,
  PrimaryButton,
  SecondaryButton,
  CompactActionButton,
  MoonNumericField,
  IconButton,
  Tag,
  cardSurface,
  useToast,
} from "@louise/moon-ui";
import { SectionTitle } from "@louise/moon-ui/primitives/SectionTitle";
import {
  SCREEN_PADDING_TOP,
  gradients,
  semanticColors,
} from "@louise/moon-ui/theme/tokens";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { EMBER } from "../../onboarding/theme";
import { CycleIllustration } from "../components/CycleIllustration";
import StatusBadge from "../components/StatusBadge";
import {
  OrganicMiniIcon,
  type OrganicMiniIconKind,
} from "@src/packages/homepage/plan/components/OrganicMiniIcon";
import { HorizontalSnapCarousel } from "@src/packages/homepage/plan/components/HorizontalSnapCarousel";
import {
  OutcomeDotFunnelRows,
  type OutcomeDotFunnelStage,
} from "@src/packages/homepage/plan/components/OutcomeDotFunnelRows";
import {
  OUTCOME_FUNNEL_ILLUSTRATIONS,
  type OutcomeFunnelIllustrationKey,
} from "@src/packages/homepage/plan/components/OutcomeFunnelIllustrations";
import {
  useCreatePhase,
  useUpdatePhase,
} from "@src/packages/patients/hooks/usePhases";
import { useDocuments } from "@src/packages/documents/hooks/useDocuments";
import { createIdempotencyKey } from "@src/packages/care/cryovault/useSpecimens";
import UpdatePregnancyModal from "./care/modals/UpdatePregnancyModal";
import { pregnancyStatusErrorMessage } from "./care/modals/betaResultErrorMessage";
import {
  buildPatientPregnancyStatusWrite,
  summarisePregnancyStatus,
  type PregnancyStatus,
} from "@src/packages/cycles/presenters/pregnancyStatusUpdate";
import {
  resolveOutcomeWriteRequestId,
  pregnancyStatusWriteTarget,
  type OutcomeWriteRequestId,
} from "@src/packages/cycles/presenters/patientOutcomeWrite";
import {
  CyclePregnancyStatusWriteError,
  useCyclePregnancyStatus,
  useRecordCyclePregnancyStatus,
} from "@src/packages/homepage/pregnancy";
import { useCareTeam } from "@src/packages/care/hooks/useCareTeam";
import { usePatientMedications } from "@src/packages/care/hooks/usePatientMedications";
import { usePatientNotes } from "@src/packages/care/hooks/usePatientNotes";
import { isPregnancyDetailsNote } from "@src/packages/homepage/pregnancy/pregnancyDetailsNote";
import { usePregnancyDetailsNote } from "@src/packages/homepage/pregnancy/usePregnancyDetailsNote";
import { useMedicationDoseLogs } from "@src/packages/care/useCalendarData";
import { useTestResults } from "@src/packages/test-results/hooks/useTestResults";
import {
  useSpecimens,
  type Specimen as ApiSpecimen,
} from "@src/packages/care/cryovault/useSpecimens";
import {
  extractObservedResults,
  resolvePostCycleLifecycle,
} from "@src/packages/cycles/presenters/postCycleState";
import { AddSpecimenModal } from "./care/modals/AddSpecimenModal";
import EditPastCycleDetailsDialog from "./care/modals/EditPastCycleDetailsDialog";
import CycleClinicTeamCard, {
  buildCycleTeamMembers,
} from "./care/sections/CycleClinicTeamCard";
import CycleProtocolSection from "./care/sections/CycleProtocolSection";
import type {
  CycleProtocolData,
  CycleProtocolDoseLog,
  CycleProtocolMedication,
  CycleProtocolMedicationPeriod,
} from "./care/sections/CycleProtocolSection";
import { AddMedicationModal } from "./care/modals/AddMedicationModal";
import type { EditMedicationData } from "./care/modals/AddMedicationModal";
import {
  deriveRecurrence,
  type MedicationScheduleItem,
  type MedForm,
} from "./care/schedule.types";
import OvarianStimulationMonitoringSection from "./care/sections/OvarianStimulationMonitoringSection";
import { isMonitoredCycleType } from "@src/packages/cycles";
import CycleTestsSection from "./care/sections/CycleTestsSection";
import ViewAllLink from "../components/ViewAllLink";
import CycleRelatedDocumentsSection, {
  getCycleDocumentFilterLabel,
  getCycleDocuments,
  getCycleDocumentTag,
} from "./care/sections/CycleRelatedDocumentsSection";
import { DocumentRow } from "./documents/DocumentRow";
import { useStartTreatmentController } from "../StartTreatmentController";
import {
  CryoSpecimenDetailModal,
  buildCryoSpecimenLabel,
  mapApiSpecimensToCryoDetails,
} from "../components/CryoSpecimenDetailModal";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
type Props = NativeStackScreenProps<CareStackParamList, "PastCycleDetail">;

type CycleOutcome =
  | "positive"
  | "negative"
  | "cancelled"
  | "closed"
  | "completed"
  | "ongoing"
  | "frozen";

type CycleNote = {
  date: string;
  dayLabel: string;
  text: string;
};

type StimDay = Record<string, string> & {
  day: string;
  date: string;
};

type ProtocolChange = {
  day: string;
  note: string;
};

type JourneyPhase = {
  label: string;
  icon: LucideIcon;
  timing: string;
  description: string;
};

type PastCycle = {
  id: string;
  cycleType: string;
  cycleTypeRaw?: string;
  cycleNumber: number;
  startDate: string;
  endDate?: string;
  clinicName: string;
  doctorName?: string | null;
  outcome: CycleOutcome;
  outcomeSummary: string;
  cancellationReason?: string | null;
  outcomeNotes?: string | null;
  keyStats?: string[];
  protocol?: string | null;
  medications?: string[] | null;
  eggsRetrieved?: number | null;
  eggsMature?: number | null;
  fertilized?: number | null;
  embryosDay3?: number | null;
  blastocystCount?: number | null;
  blastocysts?: number | null;
  embryosTransferred?: number | null;
  pgtTested?: number | null;
  euploid?: number | null;
  liningAtTransfer?: string | null;
  notes?: CycleNote[] | null;
  stimSheet?: {
    rows: { label: string; key: string }[];
    data: StimDay[];
  } | null;
  protocolChanges?: ProtocolChange[] | null;
  journeyPhases?: JourneyPhase[] | null;
};

type LabCryoMetricKey =
  | "eggsRetrieved"
  | "eggsMature"
  | "fertilized"
  | "embryosDay3"
  | "blastocystCount"
  | "embryosTransferred"
  | "blastocysts";

type LabCryoMetricValues = Record<LabCryoMetricKey, number | null>;

const LAB_CRYO_METRIC_FIELDS: {
  key: LabCryoMetricKey;
  label: string;
  metricType: string;
  defaultPhaseType: string;
}[] = [
  {
    key: "eggsRetrieved",
    label: "Eggs retrieved",
    metricType: "retrieved_oocytes",
    defaultPhaseType: "egg_retrieval",
  },
  {
    key: "eggsMature",
    label: "Mature eggs",
    metricType: "m2_count",
    defaultPhaseType: "egg_retrieval",
  },
  {
    key: "fertilized",
    label: "Fertilized",
    metricType: "fertilized_oocytes",
    defaultPhaseType: "fertilization",
  },
  {
    key: "embryosDay3",
    label: "Day 3 embryos",
    metricType: "embryos_day_3",
    defaultPhaseType: "embryo_development",
  },
  {
    key: "blastocystCount",
    label: "Blastocysts",
    metricType: "embryos_day_5",
    defaultPhaseType: "embryo_development",
  },
  {
    key: "embryosTransferred",
    label: "Embryos transferred",
    metricType: "transferred_embryo",
    defaultPhaseType: "transfer",
  },
  {
    key: "blastocysts",
    label: "Embryos frozen",
    metricType: "frozen_embryo",
    defaultPhaseType: "embryo_development",
  },
];

type LabCryoDialogStep = "retrieval" | "fertilization" | "embryo";

function getLabCryoFieldConfig(key: LabCryoMetricKey) {
  return LAB_CRYO_METRIC_FIELDS.find((field) => field.key === key)!;
}

const LAB_CRYO_LABEL_I18N: Record<LabCryoMetricKey, string> = {
  eggsRetrieved: "care.past_cycle_detail.labels.eggs_retrieved",
  eggsMature: "care.past_cycle_detail.labels.mature_eggs",
  fertilized: "care.past_cycle_detail.labels.fertilized",
  embryosDay3: "care.past_cycle_detail.labels.day3_embryos",
  blastocystCount: "care.past_cycle_detail.labels.blastocysts",
  embryosTransferred: "care.past_cycle_detail.labels.embryos_transferred",
  blastocysts: "care.past_cycle_detail.labels.embryos_frozen",
};

function getLabCryoFieldLabel(
  key: LabCryoMetricKey,
  cycle: PastCycle | null,
  t?: (path: string, opts?: { params?: Record<string, string> }) => string,
) {
  if (key === "blastocysts" && cycle?.cycleTypeRaw === "egg_freezing") {
    return t ? t("care.past_cycle_detail.labels.eggs_frozen") : "Eggs frozen";
  }
  return t ? t(LAB_CRYO_LABEL_I18N[key]) : getLabCryoFieldConfig(key).label;
}

function normalizeLabCryoMetricValues(
  values: LabCryoMetricValues,
  cycle: PastCycle | null,
): LabCryoMetricValues {
  const next = { ...values };
  const isEggFreezing = cycle?.cycleTypeRaw === "egg_freezing";
  const isEmbryoFreezing = cycle?.cycleTypeRaw === "embryo_freezing";

  if (
    next.eggsRetrieved != null &&
    next.eggsMature != null &&
    next.eggsMature > next.eggsRetrieved
  ) {
    next.eggsMature = next.eggsRetrieved;
  }

  if (next.eggsRetrieved === 0) {
    next.eggsMature = 0;
  }

  if (isEggFreezing) {
    next.fertilized = null;
    next.embryosDay3 = null;
    next.blastocystCount = null;
    next.embryosTransferred = null;
    if (next.eggsRetrieved === 0 || next.eggsMature === 0) {
      next.blastocysts = null;
    }
    return next;
  }

  if (next.eggsRetrieved === 0 || next.eggsMature === 0) {
    next.fertilized = null;
    next.embryosDay3 = null;
    next.blastocystCount = null;
    next.embryosTransferred = null;
    next.blastocysts = null;
    return next;
  }

  if (
    next.eggsMature != null &&
    next.fertilized != null &&
    next.fertilized > next.eggsMature
  ) {
    next.fertilized = next.eggsMature;
  }

  if (next.fertilized === 0) {
    next.embryosDay3 = null;
    next.blastocystCount = null;
    next.embryosTransferred = null;
    next.blastocysts = null;
    return next;
  }

  if (
    next.fertilized != null &&
    next.embryosDay3 != null &&
    next.embryosDay3 > next.fertilized
  ) {
    next.embryosDay3 = next.fertilized;
  }

  if (
    next.blastocystCount != null &&
    next.blastocystCount >
      (next.embryosDay3 ?? next.fertilized ?? next.blastocystCount)
  ) {
    next.blastocystCount = next.embryosDay3 ?? next.fertilized ?? null;
  }

  const embryoOutcomeMax = next.blastocystCount ?? next.fertilized ?? 999;
  if (next.blastocysts != null && next.blastocysts > embryoOutcomeMax) {
    next.blastocysts = embryoOutcomeMax;
  }
  if (
    next.embryosTransferred != null &&
    next.embryosTransferred > embryoOutcomeMax
  ) {
    next.embryosTransferred = embryoOutcomeMax;
  }

  if (isEmbryoFreezing) {
    next.embryosTransferred = null;
  }

  return next;
}

const TEXT = semanticColors.text.primary;
const TEXT_MID = semanticColors.text.secondary;
const TEXT_LIGHT = semanticColors.text.tertiary;

// IVF-like cycle types for showing egg/embryo metrics. Normalize because some
// app surfaces still use combined labels like "IVF + ICSI" / "ivf_icsi".
const IVF_TYPES = new Set([
  "ivf",
  "icsi",
  "ivf_icsi",
  "egg_freezing",
  "embryo_freezing",
]);

function normalizeCycleType(value: string | null | undefined) {
  return (value ?? "")
    .toLowerCase()
    .replace(/\bcycle\b/g, "")
    .replace(/[+&]/g, "_")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function isIvfLikeCycleType(...values: (string | null | undefined)[]) {
  return values.some((value) => {
    const normalized = normalizeCycleType(value);
    return (
      IVF_TYPES.has(normalized) ||
      (normalized.includes("ivf") && normalized.includes("icsi"))
    );
  });
}

type PastCycleIceSpecimenType = "oocyte" | "embryo" | "semen";
type PastCycleIceSpecimen = {
  id: string;
  type: PastCycleIceSpecimenType;
  label: string;
  dateLabel: string;
  facility: string | null;
  organicIcon: OrganicMiniIconKind;
  isUsed: boolean;
  badge?: {
    label: string;
    tone: "neutral" | "gold" | "coral" | "green" | "blue";
  };
};

const ICE_SPECIMEN_TYPE_ORDER: PastCycleIceSpecimenType[] = [
  "oocyte",
  "embryo",
  "semen",
];
function getSpecimenStorageLocation(specimen: ApiSpecimen) {
  return (
    specimen.storageLocations.find(
      (location) => location.isActive && location.storageType === "frozen",
    ) ??
    specimen.storageLocations.find(
      (location) => location.storageType === "frozen",
    ) ??
    null
  );
}

function getIceSpecimenIcon(
  type: PastCycleIceSpecimenType,
): OrganicMiniIconKind {
  if (type === "oocyte") return "eggs";
  if (type === "embryo") return "genetics";
  return "sperm";
}

function getIceSpecimenSingular(type: PastCycleIceSpecimenType) {
  if (type === "oocyte") return "Egg";
  if (type === "embryo") return "Embryo";
  return "Sperm";
}

function getIceSpecimenGroupLabel(
  type: PastCycleIceSpecimenType,
  count: number,
) {
  if (type === "oocyte") return `${count} ${count === 1 ? "egg" : "eggs"}`;
  if (type === "embryo") {
    return `${count} ${count === 1 ? "embryo" : "embryos"}`;
  }
  return `${count} ${count === 1 ? "sperm sample" : "sperm samples"}`;
}

function getIceSpecimenBadge(
  specimen: ApiSpecimen,
  t?: (path: string, opts?: { params?: Record<string, string> }) => string,
) {
  const activeFrozen = specimen.storageLocations.some(
    (location) => location.isActive && location.storageType === "frozen",
  );
  if (specimen.status === "stored" && activeFrozen) return undefined;
  const label = (path: string, fallback: string) => (t ? t(path) : fallback);
  if (specimen.status === "used") {
    return {
      label: label(
        "care.past_cycle_detail.specimen_status.used_later",
        "Used later",
      ),
      tone: "gold" as const,
    };
  }
  if (specimen.status === "discarded") {
    return {
      label: label(
        "care.past_cycle_detail.specimen_status.discarded",
        "Discarded",
      ),
      tone: "coral" as const,
    };
  }
  if (specimen.status === "donated") {
    return {
      label: label("care.past_cycle_detail.specimen_status.donated", "Donated"),
      tone: "green" as const,
    };
  }
  if (specimen.status === "in_transit") {
    return {
      label: label(
        "care.past_cycle_detail.specimen_status.in_transit",
        "In transit",
      ),
      tone: "blue" as const,
    };
  }
  return {
    label: label(
      "care.past_cycle_detail.specimen_status.not_active",
      "Not active",
    ),
    tone: "neutral" as const,
  };
}

function buildPastCycleIceSpecimens(
  specimens: ApiSpecimen[] | undefined,
  cycle: PastCycle | null,
  locale: Locale,
  t?: (path: string, opts?: { params?: Record<string, string> }) => string,
) {
  if (!specimens || !cycle) return [];

  const counters: Partial<Record<PastCycleIceSpecimenType, number>> = {};
  return specimens
    .filter((specimen) => {
      const hasFrozenHistory = specimen.storageLocations.some(
        (location) => location.storageType === "frozen",
      );
      return (
        specimen.cycleId === cycle.id &&
        (specimen.type === "oocyte" ||
          specimen.type === "embryo" ||
          specimen.type === "semen") &&
        (hasFrozenHistory ||
          specimen.status === "stored" ||
          specimen.status === "in_transit")
      );
    })
    .sort((a, b) => {
      const typeCompare =
        ICE_SPECIMEN_TYPE_ORDER.indexOf(a.type as PastCycleIceSpecimenType) -
        ICE_SPECIMEN_TYPE_ORDER.indexOf(b.type as PastCycleIceSpecimenType);
      if (typeCompare !== 0) return typeCompare;
      const dateA = getSpecimenStorageLocation(a)?.storedFrom ?? a.collectedAt;
      const dateB = getSpecimenStorageLocation(b)?.storedFrom ?? b.collectedAt;
      return dateA.localeCompare(dateB);
    })
    .map((specimen): PastCycleIceSpecimen => {
      const type = specimen.type as PastCycleIceSpecimenType;
      counters[type] = (counters[type] ?? 0) + 1;
      const storage = getSpecimenStorageLocation(specimen);
      const date = storage?.storedFrom ?? specimen.collectedAt;
      return {
        id: specimen.id,
        type,
        label: `${getIceSpecimenSingular(type)} #${counters[type]}`,
        dateLabel: date
          ? formatShortDate(date, locale)
          : t
            ? t("care.cycle_documents.no_date")
            : "No date",
        facility: storage?.facility ?? null,
        organicIcon: getIceSpecimenIcon(type),
        isUsed: specimen.status === "used",
        badge: getIceSpecimenBadge(specimen, t),
      };
    });
}

// Phase type → icon mapping
const PHASE_ICONS: Record<string, LucideIcon> = {
  pre_treatment: ClipboardCheck,
  protocol: Syringe,
  egg_retrieval: Target,
  sperm_collection: Droplets,
  fertilization: Sparkles,
  insemination: Droplets,
  embryo_development: Beaker,
  transfer: Baby,
  luteal_phase: Hourglass,
};

// ── Date formatter ──

function formatShortDate(dateStr: string | null, locale: Locale): string {
  if (!dateStr) return "—";
  return formatDate(dateStr, locale, {
    month: "short",
    day: "numeric",
  });
}

function formatMonthYear(dateStr: string | null, locale: Locale): string {
  if (!dateStr) return "—";
  return formatDate(dateStr, locale, {
    month: "short",
    year: "numeric",
  });
}

function toApiDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// ── Helpers ──

type CompletionItem = {
  label: string;
  complete: boolean;
};

function hasText(value: string | null | undefined) {
  return !!value && value !== "—";
}

function hasCycleOutcomeDetails(cycle: PastCycle) {
  const normalizedType = normalizeCycleType(
    cycle.cycleTypeRaw ?? cycle.cycleType,
  );
  const hasRecordedOutcome =
    cycle.outcome !== "closed" ||
    hasText(cycle.outcomeSummary) ||
    hasText(cycle.outcomeNotes);

  if (normalizedType === "fet") {
    return cycle.embryosTransferred != null || hasRecordedOutcome;
  }

  if (
    normalizedType === "iui" ||
    normalizedType === "oi" ||
    normalizedType === "ovulation_induction"
  ) {
    return hasRecordedOutcome;
  }

  if (normalizedType === "sperm_freezing") {
    return hasRecordedOutcome;
  }

  if (
    normalizedType === "egg_freezing" ||
    normalizedType === "embryo_freezing"
  ) {
    return (
      cycle.blastocysts != null ||
      cycle.outcome === "cancelled" ||
      hasText(cycle.outcomeNotes)
    );
  }

  return (
    cycle.eggsRetrieved != null ||
    cycle.eggsMature != null ||
    cycle.fertilized != null ||
    cycle.embryosTransferred != null ||
    cycle.blastocysts != null ||
    hasRecordedOutcome
  );
}

function getCycleCompletionItems(
  cycle: PastCycle,
  isIVF: boolean,
): CompletionItem[] {
  const items: CompletionItem[] = [
    {
      label: "Cycle type",
      complete: hasText(cycle.cycleType),
    },
    {
      label: "Cycle number",
      complete: cycle.cycleNumber > 0,
    },
    {
      label: "Outcome",
      complete:
        cycle.outcome !== "closed" ||
        hasText(cycle.outcomeSummary) ||
        !!cycle.keyStats?.length,
    },
    {
      label: "Dates and clinic",
      complete:
        hasText(cycle.clinicName) &&
        hasText(cycle.startDate) &&
        hasText(cycle.endDate),
    },
    {
      label: "Protocol",
      complete:
        hasText(cycle.protocol) ||
        !!cycle.medications?.length ||
        !!cycle.journeyPhases?.length ||
        !!cycle.notes?.length ||
        !!cycle.protocolChanges?.length,
    },
  ];

  items.push({
    label: "Outcomes",
    complete: hasCycleOutcomeDetails(cycle),
  });

  if (isIVF) {
    items.push({
      label: "Ovarian monitoring",
      complete: !!cycle.stimSheet?.data.length,
    });
  }

  return items;
}

const frozenSpecimenIcon =
  require("../../assets/images/egg-freezing-snowflake-icon.png") as number;
const protocolVialSyringeIcon =
  require("../../assets/images/vial-syringe.png") as number;

function getKnownCycleValue(value: string | null | undefined) {
  if (!value || value === "—") return null;
  return value;
}

/** Says that filling in a past cycle is not available yet. */
function usePastCycleBackfillToast() {
  const t = useTranslations();
  const { showToast } = useToast();
  return useCallback(() => {
    showToast({
      variant: "info",
      message: t("care.past_cycle_detail.backfill_alert_body"),
    });
  }, [showToast, t]);
}

export function AddPrompt({
  label,
  subtitle,
  onPress,
}: {
  label: string;
  subtitle?: string;
  onPress?: () => void;
}) {
  const showBackfillToast = usePastCycleBackfillToast();
  return (
    <DocumentRow
      title={label}
      subtitle={subtitle}
      imageSource={protocolVialSyringeIcon}
      trailing={
        <Plus size={18} color={EMBER.colors.textLight} strokeWidth={1.5} />
      }
      onPress={onPress ?? showBackfillToast}
      showChevron={false}
    />
  );
}

function resolveLabCryoFieldForStage(
  stageKey: string | undefined,
): LabCryoMetricKey | undefined {
  if (!stageKey) return undefined;
  switch (stageKey) {
    case "retrieved":
      return "eggsRetrieved";
    case "mature":
      return "eggsMature";
    case "fertilized":
      return "fertilized";
    case "day-3":
      return "embryosDay3";
    case "blastocysts":
      return "blastocystCount";
    case "frozen":
      return "blastocysts";
    case "transferred":
      return "embryosTransferred";
    default:
      return undefined;
  }
}

function getLabCryoMetricValues(cycle: PastCycle | null): LabCryoMetricValues {
  return {
    eggsRetrieved: cycle?.eggsRetrieved ?? null,
    eggsMature: cycle?.eggsMature ?? null,
    fertilized: cycle?.fertilized ?? null,
    embryosDay3: cycle?.embryosDay3 ?? null,
    blastocystCount: cycle?.blastocystCount ?? null,
    embryosTransferred: cycle?.embryosTransferred ?? null,
    blastocysts: cycle?.blastocysts ?? null,
  };
}

function LabCryoMetricsDialog({
  visible,
  onClose,
  cycle,
  onSave,
  saving,
  error,
  focusField,
}: {
  visible: boolean;
  onClose: () => void;
  cycle: PastCycle | null;
  onSave: (values: LabCryoMetricValues) => void;
  saving: boolean;
  error: string | null;
  focusField?: LabCryoMetricKey;
}) {
  const t = useTranslations();
  const { height: windowHeight } = useWindowDimensions();
  const [draft, setDraft] = useState<LabCryoMetricValues>(() =>
    normalizeLabCryoMetricValues(getLabCryoMetricValues(cycle), cycle),
  );
  const [stepIndex, setStepIndex] = useState(0);
  const isEggFreezing = cycle?.cycleTypeRaw === "egg_freezing";
  const isEmbryoFreezing = cycle?.cycleTypeRaw === "embryo_freezing";

  useEffect(() => {
    if (visible) {
      const nextDraft = normalizeLabCryoMetricValues(
        getLabCryoMetricValues(cycle),
        cycle,
      );
      setDraft(nextDraft);
      setStepIndex(0);
    }
  }, [cycle, visible]);

  const updateDraft = (key: LabCryoMetricKey, value: number | null) => {
    setDraft((current) =>
      normalizeLabCryoMetricValues({ ...current, [key]: value }, cycle),
    );
  };

  const modalMaxHeight = Math.min(windowHeight * 0.86, 620);
  const scrollMaxHeight = Math.max(280, Math.min(windowHeight * 0.62, 470));
  const steps = useMemo<LabCryoDialogStep[]>(() => {
    if (isEggFreezing) return ["retrieval"];
    const nextSteps: LabCryoDialogStep[] = ["retrieval"];
    if (draft.eggsMature != null && draft.eggsMature > 0) {
      nextSteps.push("fertilization");
    }
    if (draft.fertilized != null && draft.fertilized > 0) {
      nextSteps.push("embryo");
    }
    return nextSteps;
  }, [draft.eggsMature, draft.fertilized, isEggFreezing]);

  useEffect(() => {
    if (stepIndex > steps.length - 1) {
      setStepIndex(Math.max(0, steps.length - 1));
    }
  }, [stepIndex, steps.length]);

  const activeStep = steps[stepIndex] ?? "retrieval";
  const stepCopy: Record<
    LabCryoDialogStep,
    { title: string; subtitle: string }
  > = {
    retrieval: {
      title: t("care.past_cycle_detail.lab_cryo_dialog.retrieval_title"),
      subtitle: t("care.past_cycle_detail.lab_cryo_dialog.retrieval_subtitle"),
    },
    fertilization: {
      title: t("care.past_cycle_detail.lab_cryo_dialog.fertilization_title"),
      subtitle: t(
        "care.past_cycle_detail.lab_cryo_dialog.fertilization_subtitle",
      ),
    },
    embryo: {
      title: t("care.past_cycle_detail.lab_cryo_dialog.embryo_title"),
      subtitle: t("care.past_cycle_detail.lab_cryo_dialog.embryo_subtitle"),
    },
  };
  const isFocused = focusField != null;
  const activeCopy = isFocused
    ? {
        title: getLabCryoFieldLabel(focusField, cycle, t),
        subtitle: "",
      }
    : stepCopy[activeStep];
  const currentFields = useMemo<LabCryoMetricKey[]>(() => {
    if (isFocused) return [focusField];
    if (activeStep === "retrieval") {
      return isEggFreezing && draft.eggsMature != null && draft.eggsMature > 0
        ? ["eggsRetrieved", "eggsMature", "blastocysts"]
        : ["eggsRetrieved", "eggsMature"];
    }
    if (activeStep === "fertilization") {
      return ["fertilized"];
    }
    return isEmbryoFreezing
      ? ["embryosDay3", "blastocystCount", "blastocysts"]
      : ["embryosDay3", "blastocystCount", "blastocysts", "embryosTransferred"];
  }, [
    activeStep,
    draft.eggsMature,
    isEggFreezing,
    isEmbryoFreezing,
    isFocused,
    focusField,
  ]);
  const requiredFields: LabCryoMetricKey[] = isFocused
    ? [focusField]
    : activeStep === "retrieval"
      ? ["eggsRetrieved", "eggsMature"]
      : activeStep === "fertilization"
        ? ["fertilized"]
        : [];
  const canContinue =
    !saving && requiredFields.every((field) => draft[field] != null);
  const isLastStep = isFocused || stepIndex >= steps.length - 1;
  const branchNote = isFocused
    ? null
    : activeStep === "retrieval" &&
        (draft.eggsRetrieved === 0 || draft.eggsMature === 0)
      ? "This stopped at retrieval, so fertilization, transfer, and cryo details will be skipped."
      : activeStep === "fertilization" && draft.fertilized === 0
        ? "This stopped at fertilization, so embryo, transfer, and cryo details will be skipped."
        : null;

  const getFieldMax = (key: LabCryoMetricKey) => {
    if (key === "eggsMature") return draft.eggsRetrieved ?? 999;
    if (key === "fertilized") return draft.eggsMature ?? 999;
    if (key === "embryosDay3") return draft.fertilized ?? 999;
    if (key === "blastocystCount") {
      return draft.embryosDay3 ?? draft.fertilized ?? 999;
    }
    if (key === "blastocysts" && isEggFreezing) return draft.eggsMature ?? 999;
    if (key === "blastocysts" || key === "embryosTransferred") {
      return draft.blastocystCount ?? draft.fertilized ?? 999;
    }
    return 999;
  };

  const handleBack = () => {
    if (stepIndex === 0) {
      onClose();
      return;
    }
    setStepIndex((current) => Math.max(0, current - 1));
  };

  const handlePrimaryPress = () => {
    const normalized = normalizeLabCryoMetricValues(draft, cycle);
    setDraft(normalized);
    if (isLastStep) {
      onSave(normalized);
      return;
    }
    setStepIndex((current) => Math.min(current + 1, steps.length - 1));
  };

  return (
    <Dialog
      size="wide"
      visible={visible}
      onClose={onClose}
      closeButton="minimal"
      surfaceStyle={{ maxHeight: modalMaxHeight }}
      contentStyle={{
        paddingTop: 30,
        paddingHorizontal: 22,
        paddingBottom: 18,
        maxHeight: modalMaxHeight,
      }}
    >
      {stepIndex > 0 ? (
        <IconButton
          accessibilityLabel={t("care.past_cycle_detail.lab_cryo_dialog.back")}
          action="close_past_cycle_sheet"
          onPress={handleBack}
          hitSlop={8}
          disabled={saving}
          disabledOpacity={0.5}
          size="sm"
          surface="bare"
          style={{
            position: "absolute",
            top: 12,
            left: 12,
            zIndex: 2,
          }}
        >
          <ChevronLeft size={20} color={TEXT_LIGHT} strokeWidth={2} />
        </IconButton>
      ) : null}
      <View style={{ maxHeight: "100%" }}>
        <ScrollView
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          style={{ maxHeight: scrollMaxHeight }}
          contentContainerStyle={{ paddingBottom: 14 }}
        >
          <View style={{ alignItems: "center" }}>
            <View style={{ marginBottom: 10 }}>
              <OrganicMiniIcon kind="cryo" size={44} />
            </View>
            <DialogHeader
              align="center"
              title={activeCopy.title}
              subtitle={activeCopy.subtitle}
            />
          </View>

          <View style={{ marginTop: 16, gap: 10 }}>
            {currentFields.map((key) => (
              <MoonNumericField
                key={key}
                label={getLabCryoFieldLabel(key, cycle, t)}
                value={draft[key]}
                onChange={(value) => updateDraft(key, value)}
                min={0}
                max={getFieldMax(key)}
                disabled={saving}
                textInputProps={{ returnKeyType: "done" }}
              />
            ))}
          </View>

          {branchNote ? (
            <Text
              className="font-dm-sans"
              style={{
                marginTop: 12,
                fontSize: 12,
                lineHeight: 17,
                color: TEXT_LIGHT,
                fontWeight: "600",
                textAlign: "center",
              }}
            >
              {branchNote}
            </Text>
          ) : null}

          {error ? (
            <ActionBanner
              variant="important"
              message={error}
              style={{ marginTop: 12 }}
            />
          ) : null}
        </ScrollView>

        <View
          style={{
            flexDirection: "row",
            gap: 10,
            marginTop: 10,
          }}
        >
          <SecondaryButton
            label={
              stepIndex > 0
                ? t("care.past_cycle_detail.lab_cryo_dialog.back")
                : t("care.past_cycle_detail.lab_cryo_dialog.cancel")
            }
            onPress={handleBack}
            flex={1}
          />
          <PrimaryButton
            label={
              isLastStep
                ? t("care.past_cycle_detail.lab_cryo_dialog.save")
                : t("care.past_cycle_detail.lab_cryo_dialog.next")
            }
            onPress={handlePrimaryPress}
            loading={saving}
            disabled={!canContinue}
            flex={1}
          />
        </View>
      </View>
    </Dialog>
  );
}

function getPregnancyOutcomeFunnelCopy(cycle: PastCycle) {
  if (cycle.outcome === "positive") {
    return {
      value: Math.max(cycle.embryosTransferred ?? 1, 1),
      displayValue: "β+",
      detail: cycle.outcomeSummary || "Positive pregnancy result recorded.",
      missing: false,
    };
  }
  if (cycle.outcome === "negative") {
    return {
      value: 0,
      displayValue: "0",
      detail: "No pregnancy recorded from this transfer.",
      missing: false,
    };
  }
  if (cycle.outcome === "cancelled") {
    return {
      value: 0,
      displayValue: "0",
      detail:
        cycle.outcomeSummary || "Cycle cancelled before pregnancy result.",
      missing: false,
    };
  }
  return {
    value: 0,
    displayValue: "+",
    detail: "Add pregnancy outcome.",
    missing: true,
  };
}

function getFetEmbryoDetailCopy(cycle: PastCycle) {
  const details: string[] = [];

  if (cycle.blastocystCount != null) {
    details.push(`${cycle.blastocystCount} blastocyst`);
  }
  if (cycle.pgtTested != null) {
    details.push(`${cycle.pgtTested} PGT tested`);
  }
  if (cycle.euploid != null) {
    details.push(`${cycle.euploid} euploid`);
  }

  if (details.length > 0) {
    return {
      value: Math.max(cycle.embryosTransferred ?? 1, 1),
      displayValue: details.join(" · "),
      detail: "Transferred embryo details recorded for this FET.",
      missing: false,
    };
  }

  return {
    value: 0,
    displayValue: "+",
    detail: "Add embryo grade, day, PGT status, or source cycle.",
    missing: true,
  };
}

type ProcedureFunnelCopy = {
  value: number;
  detail: string;
  missingDetail: string;
  missing: boolean;
};

function getProcedureFunnelCopy(
  cycle: PastCycle,
  procedureLabel: string,
): ProcedureFunnelCopy {
  const procedure = procedureLabel.toLowerCase();

  if (cycle.outcome === "cancelled") {
    return {
      value: 0,
      detail:
        cycle.outcomeSummary ||
        `${procedureLabel} was not completed because this cycle was cancelled.`,
      missingDetail: `Add ${procedure} details.`,
      missing: false,
    };
  }

  if (cycle.outcome === "closed") {
    return {
      value: 0,
      detail: `Add ${procedure} details.`,
      missingDetail: `Add ${procedure} details.`,
      missing: true,
    };
  }

  return {
    value: 1,
    detail:
      cycle.outcome === "ongoing"
        ? `${procedureLabel} recorded; this cycle is still in progress.`
        : `${procedureLabel} recorded for this cycle.`,
    missingDetail: `Add ${procedure} details.`,
    missing: false,
  };
}

function getSpermFreezingFunnelCopy(
  cycle: PastCycle,
  stage: "collection" | "storage",
): ProcedureFunnelCopy {
  const isStorage = stage === "storage";
  const addDetails = isStorage
    ? "Add storage or post-thaw details."
    : "Add sperm collection details.";

  if (cycle.outcome === "cancelled") {
    return {
      value: 0,
      detail:
        cycle.outcomeSummary ||
        "Sperm freezing was not completed because this cycle was cancelled.",
      missingDetail: addDetails,
      missing: false,
    };
  }

  if (cycle.outcome === "closed") {
    return {
      value: 0,
      detail: addDetails,
      missingDetail: addDetails,
      missing: true,
    };
  }

  if (cycle.outcome === "positive" || cycle.outcome === "negative") {
    return {
      value: 0,
      detail: addDetails,
      missingDetail: addDetails,
      missing: true,
    };
  }

  if (cycle.outcome === "ongoing" && isStorage) {
    return {
      value: 0,
      detail: "Sperm was collected; storage is still being confirmed.",
      missingDetail: addDetails,
      missing: true,
    };
  }

  return {
    value: 1,
    detail:
      cycle.outcome === "ongoing"
        ? "Sperm collection recorded; freezing is still in progress."
        : cycle.outcome === "frozen"
          ? isStorage
            ? "Sperm frozen and storage recorded for this cycle."
            : "Sperm collection recorded for this cycle."
          : cycle.outcome === "completed"
            ? isStorage
              ? "Sperm freezing cycle completed; storage outcome recorded."
              : "Sperm collection recorded for this cycle."
            : isStorage
              ? "Sperm storage outcome recorded for this cycle."
              : "Sperm collection recorded for this cycle.",
    missingDetail: addDetails,
    missing: false,
  };
}

type OutcomeFunnelSourceStage = {
  key: string;
  label: string;
  value?: number | null;
  displayValue?: string;
  icon: OrganicMiniIconKind;
  illustrationKey: OutcomeFunnelIllustrationKey;
  detail: string;
  missingDetail: string;
  missing?: boolean;
};

function getOutcomeUploadCopy(cycle: PastCycle) {
  const normalizedType = normalizeCycleType(
    cycle.cycleTypeRaw ?? cycle.cycleType,
  );

  if (normalizedType === "fet") {
    return {
      title: "Upload transfer or beta report",
      subtitle:
        "We can parse embryo grade, day, PGT status, source, transfer details, beta results, and pregnancy outcome into this cycle.",
    };
  }

  if (
    normalizedType === "iui" ||
    normalizedType === "oi" ||
    normalizedType === "ovulation_induction"
  ) {
    return {
      title: "Upload monitoring or outcome report",
      subtitle:
        "We can parse medication response, insemination or timing details, beta results, and pregnancy outcome into this cycle.",
    };
  }

  if (normalizedType === "sperm_freezing") {
    return {
      title: "Upload semen analysis or freezing report",
      subtitle:
        "We can parse collection, semen analysis, cryo storage, and post-thaw details into this cycle.",
    };
  }

  if (normalizedType === "egg_freezing") {
    return {
      title: "Upload lab or freezing report",
      subtitle:
        "We can parse retrieval, maturity, freezing, storage, and cycle outcome details into this cycle.",
    };
  }

  return {
    title: "Upload lab or embryology report",
    subtitle:
      "We can parse embryo quality, grading, transfer, cryo, and outcome details into this cycle.",
  };
}

function PastCycleOutcomesFunnel({
  cycle,
  onAddRecords,
}: {
  cycle: PastCycle;
  onAddRecords: (stageKey: string) => void;
}) {
  const normalizedType = normalizeCycleType(
    cycle.cycleTypeRaw ?? cycle.cycleType,
  );
  const isFet = normalizedType === "fet";
  const isIui = normalizedType === "iui";
  const isOvulationInduction =
    normalizedType === "oi" || normalizedType === "ovulation_induction";
  const isSpermFreezing = normalizedType === "sperm_freezing";
  const isEggFreezing = normalizedType === "egg_freezing";
  const isEmbryoFreezing = normalizedType === "embryo_freezing";
  const stoppedBeforeFertilization =
    cycle.eggsRetrieved === 0 || cycle.eggsMature === 0;
  const stoppedBeforeEmbryo =
    stoppedBeforeFertilization || cycle.fertilized === 0;
  const pregnancyOutcomeCopy = getPregnancyOutcomeFunnelCopy(cycle);
  const fetEmbryoDetailCopy = getFetEmbryoDetailCopy(cycle);
  let sourceStages: OutcomeFunnelSourceStage[] = [];

  if (isFet) {
    sourceStages = [
      {
        key: "transferred",
        label: "Embryos transferred",
        value: cycle.embryosTransferred,
        icon: "process",
        illustrationKey: "embryo-transfer",
        detail: "Frozen embryo transfer recorded for this cycle.",
        missingDetail: "Add embryo transfer details.",
      },
      {
        key: "embryo-details",
        label: "Embryo details",
        value: fetEmbryoDetailCopy.value,
        displayValue: fetEmbryoDetailCopy.displayValue,
        icon: "genetics",
        illustrationKey: "day-5-embryos",
        detail: fetEmbryoDetailCopy.detail,
        missingDetail: fetEmbryoDetailCopy.detail,
        missing: fetEmbryoDetailCopy.missing,
      },
      {
        key: "pregnancy-outcome",
        label: "Pregnancy outcome",
        value: pregnancyOutcomeCopy.value,
        displayValue: pregnancyOutcomeCopy.displayValue,
        icon: "panel",
        illustrationKey: "pregnancy-outcome",
        detail: pregnancyOutcomeCopy.detail,
        missingDetail: pregnancyOutcomeCopy.detail,
        missing: pregnancyOutcomeCopy.missing,
      },
    ];
  } else if (isIui) {
    const inseminationCopy = getProcedureFunnelCopy(cycle, "Insemination");
    sourceStages = [
      {
        key: "insemination",
        label: "Insemination",
        value: inseminationCopy.value,
        icon: "process",
        illustrationKey: "insemination",
        detail: inseminationCopy.detail,
        missingDetail: inseminationCopy.missingDetail,
        missing: inseminationCopy.missing,
      },
      {
        key: "pregnancy-outcome",
        label: "Pregnancy outcome",
        value: pregnancyOutcomeCopy.value,
        displayValue: pregnancyOutcomeCopy.displayValue,
        icon: "panel",
        illustrationKey: "pregnancy-outcome",
        detail: pregnancyOutcomeCopy.detail,
        missingDetail: pregnancyOutcomeCopy.detail,
        missing: pregnancyOutcomeCopy.missing,
      },
    ];
  } else if (isOvulationInduction) {
    const ovulationInductionCopy = getProcedureFunnelCopy(
      cycle,
      "Ovulation induction",
    );
    sourceStages = [
      {
        key: "ovulation-induction",
        label: "Ovulation induction",
        value: ovulationInductionCopy.value,
        icon: "hormones",
        illustrationKey: "ovulation-induction",
        detail: ovulationInductionCopy.detail,
        missingDetail: ovulationInductionCopy.missingDetail,
        missing: ovulationInductionCopy.missing,
      },
      {
        key: "pregnancy-outcome",
        label: "Pregnancy outcome",
        value: pregnancyOutcomeCopy.value,
        displayValue: pregnancyOutcomeCopy.displayValue,
        icon: "panel",
        illustrationKey: "pregnancy-outcome",
        detail: pregnancyOutcomeCopy.detail,
        missingDetail: pregnancyOutcomeCopy.detail,
        missing: pregnancyOutcomeCopy.missing,
      },
    ];
  } else if (isSpermFreezing) {
    const collectionCopy = getSpermFreezingFunnelCopy(cycle, "collection");
    const storageCopy = getSpermFreezingFunnelCopy(cycle, "storage");
    sourceStages = [
      {
        key: "collection",
        label: "Sperm collected",
        value: collectionCopy.value,
        icon: "sperm",
        illustrationKey: "sperm-collected",
        detail: collectionCopy.detail,
        missingDetail: collectionCopy.missingDetail,
        missing: collectionCopy.missing,
      },
      {
        key: "storage-outcome",
        label: "Storage outcome",
        value: storageCopy.value,
        icon: "cryo",
        illustrationKey: "frozen-sperm-storage",
        detail: storageCopy.detail,
        missingDetail: storageCopy.missingDetail,
        missing: storageCopy.missing,
      },
    ];
  } else {
    sourceStages = [
      {
        key: "retrieved",
        label: "Eggs retrieved",
        value: cycle.eggsRetrieved,
        icon: "eggs",
        illustrationKey: "retrieved-eggs",
        detail: "Oocytes collected at retrieval.",
        missingDetail: "Add eggs retrieved.",
      },
      {
        key: "mature",
        label: "Mature eggs",
        value: cycle.eggsMature,
        icon: "amh",
        illustrationKey: "mature-eggs",
        detail: "Eggs mature enough for freezing or fertilization.",
        missingDetail: "Add mature egg count.",
      },
    ];

    if (isEggFreezing && !stoppedBeforeFertilization) {
      sourceStages.push({
        key: "frozen",
        label: "Eggs frozen",
        value: cycle.blastocysts,
        icon: "cryo",
        illustrationKey: "frozen-eggs",
        detail: "Eggs stored from this cycle.",
        missingDetail: "Add eggs frozen.",
      });
    }

    if (!isEggFreezing && !stoppedBeforeFertilization) {
      sourceStages.push({
        key: "fertilized",
        label: "Fertilized",
        value: cycle.fertilized,
        icon: "sperm",
        illustrationKey: "fertilized-eggs",
        detail: "Eggs that fertilized after IVF or ICSI.",
        missingDetail: "Add fertilization result.",
      });
    }

    if (!isEggFreezing && !stoppedBeforeEmbryo) {
      if (cycle.embryosDay3 != null) {
        sourceStages.push({
          key: "day-3",
          label: "Day 3 embryos",
          value: cycle.embryosDay3,
          icon: "genetics",
          illustrationKey: "day-3-embryos",
          detail: "Embryos still developing on day 3.",
          missingDetail: "Add day 3 embryo count.",
        });
      }

      sourceStages.push(
        {
          key: "blastocysts",
          label: "Blastocysts",
          value: cycle.blastocystCount,
          icon: "genetics",
          illustrationKey: "day-5-embryos",
          detail: "Embryos that reached blastocyst stage.",
          missingDetail: "Add blastocyst count.",
        },
        {
          key: "frozen",
          label: "Embryos frozen",
          value: cycle.blastocysts,
          icon: "cryo",
          illustrationKey: "frozen-embryos",
          detail: "Embryos stored from this cycle.",
          missingDetail: "Add embryos frozen.",
        },
      );

      if (!isEmbryoFreezing) {
        sourceStages.push(
          {
            key: "transferred",
            label: "Embryos transferred",
            value: cycle.embryosTransferred,
            icon: "process",
            illustrationKey: "embryo-transfer",
            detail: "Embryos transferred in this cycle.",
            missingDetail: "Add transfer result.",
          },
          {
            key: "pregnancy-outcome",
            label: "Pregnancy outcome",
            value: pregnancyOutcomeCopy.value,
            displayValue: pregnancyOutcomeCopy.displayValue,
            icon: "panel",
            illustrationKey: "pregnancy-outcome",
            detail: pregnancyOutcomeCopy.detail,
            missingDetail: pregnancyOutcomeCopy.detail,
            missing: pregnancyOutcomeCopy.missing,
          },
        );
      }
    }
  }

  const funnelStages = sourceStages.map((step) => {
    const missing = step.missing ?? step.value == null;
    return {
      id: step.key,
      label: step.label,
      value: missing ? 0 : Number(step.value),
      displayValue: step.displayValue ?? (missing ? "+" : undefined),
      icon: step.icon,
      illustration: OUTCOME_FUNNEL_ILLUSTRATIONS[step.illustrationKey],
      detail: missing ? step.missingDetail : step.detail,
      missing,
      onPress: () => onAddRecords(step.key),
    };
  }) satisfies OutcomeDotFunnelStage[];

  const recordedValues = funnelStages
    .filter((stage) => !stage.missing)
    .map((stage) => stage.value);
  const maxRecordedValue =
    recordedValues.length > 0 ? Math.max(...recordedValues) : 0;
  const totalDots = maxRecordedValue > 0 ? maxRecordedValue : 12;

  return <OutcomeDotFunnelRows stages={funnelStages} totalDots={totalDots} />;
}

// ── Completion card ──

function SemiCircleCompletionMeter({ percent }: { percent: number }) {
  const clamped = Math.min(100, Math.max(0, percent));
  const cx = 76;
  const cy = 92;
  const r = 66;
  const startAngle = Math.PI * 1.08;
  const endAngleBase = -Math.PI * 0.08;
  const sweep = startAngle - endAngleBase;
  const endAngle = startAngle - sweep * (clamped / 100);
  const startX = cx + r * Math.cos(startAngle);
  const startY = cy - r * Math.sin(startAngle);
  const fullEndX = cx + r * Math.cos(endAngleBase);
  const fullEndY = cy - r * Math.sin(endAngleBase);
  const endX = cx + r * Math.cos(endAngle);
  const endY = cy - r * Math.sin(endAngle);
  // The colored arc must pick the same minor/major arc as the grey track so it
  // shares the track's derived center. The gauge spans ~1.16π, so the swept
  // angle only exceeds 180° past ~86% — keying off the actual swept angle
  // (not a flat 50%) keeps the colored arc concentric with the track.
  const largeArc = sweep * (clamped / 100) > Math.PI ? 1 : 0;
  const progressPath =
    clamped <= 0
      ? ""
      : `M ${startX} ${startY} A ${r} ${r} 0 ${largeArc} 1 ${endX} ${endY}`;

  return (
    <View style={{ width: 152, height: 116, alignItems: "center" }}>
      {/* Key the SVG by the value so it remounts when the percentage changes
          between cycles — react-native-svg on web doesn't always repaint the
          arc `d` on a prop change, leaving a stale meter until a reload. */}
      <Svg key={clamped} width={152} height={116}>
        <Defs>
          <SvgLinearGradient
            id="cycleCompletionGradient"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="100%"
          >
            <Stop offset="0%" stopColor={gradients.accent.colors[0]} />
            <Stop offset="100%" stopColor={gradients.accent.colors[1]} />
          </SvgLinearGradient>
        </Defs>
        <Path
          d={`M ${startX} ${startY} A ${r} ${r} 0 1 1 ${fullEndX} ${fullEndY}`}
          stroke={semanticColors.border.strong}
          strokeWidth={8}
          strokeLinecap="round"
          fill="none"
        />
        {progressPath ? (
          <Path
            d={progressPath}
            stroke="url(#cycleCompletionGradient)"
            strokeWidth={8}
            strokeLinecap="round"
            fill="none"
          />
        ) : null}
      </Svg>
      <Text
        className="font-dm-sans"
        style={{
          position: "absolute",
          top: 46,
          fontSize: 13,
          lineHeight: 17,
          color: TEXT_LIGHT,
          fontWeight: "800",
        }}
      >
        {clamped}%
      </Text>
    </View>
  );
}

function CycleCompletionCard({
  completed,
  total,
  missing,
  onOpenExport,
  onUploadDocuments,
}: {
  completed: number;
  total: number;
  missing: CompletionItem[];
  onOpenExport: () => void;
  onUploadDocuments: () => void;
}) {
  const capturePress = usePressCapture("CycleCompletionCard");
  const t = useTranslations();
  const percent = total > 0 ? Math.round((completed / total) * 100) : 100;
  const isComplete = missing.length === 0;
  const exportLabel = t("homepage.review_concept.past_cycle_unlock_export");

  return (
    <View
      style={{
        marginTop: 16,
        paddingHorizontal: 18,
        paddingTop: 12,
        paddingBottom: 6,
        alignItems: "center",
        width: "100%",
        maxWidth: 390,
        alignSelf: "center",
      }}
    >
      <SemiCircleCompletionMeter percent={percent} />
      <View style={{ marginTop: -36, marginBottom: 10 }}>
        <OrganicMiniIcon kind="medical-records" size={54} />
      </View>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 17,
          lineHeight: 22,
          color: TEXT,
          fontWeight: "900",
          textAlign: "center",
          letterSpacing: 0,
        }}
      >
        {t("homepage.review_concept.past_cycle_record_completion_title")}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          marginTop: 5,
          fontSize: 12.5,
          lineHeight: 18,
          color: TEXT_LIGHT,
          fontWeight: "600",
          textAlign: "center",
          maxWidth: 310,
        }}
      >
        {t("homepage.review_concept.past_cycle_record_completion_body", {
          params: { completed: String(completed), total: String(total) },
        })}
      </Text>

      <Pressable
        onPress={() => {
          capturePress(
            pressLabel(`${exportLabel} (coming soon)`),
            "open_cycle_export",
          );
          onOpenExport();
        }}
        accessibilityRole="button"
        accessibilityLabel={`${exportLabel} (coming soon)`}
        style={({ pressed }) => ({
          width: "100%",
          marginTop: 16,
          borderRadius: 999,
          backgroundColor: isComplete
            ? "rgba(255,255,255,0.72)"
            : semanticColors.action.quiet.background,
          borderWidth: 1,
          borderColor: semanticColors.action.quiet.border,
          paddingVertical: 10,
          paddingHorizontal: 16,
          alignItems: "center",
          justifyContent: "center",
          gap: 4,
          opacity: pressed ? 0.74 : 1,
          minHeight: 48,
        })}
      >
        <StatusBadge type="coming-soon" />
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Lock size={12} color={TEXT_MID} strokeWidth={2} />
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              lineHeight: 16,
              color: TEXT_MID,
              fontWeight: "900",
              textAlign: "center",
            }}
          >
            {exportLabel}
          </Text>
        </View>
      </Pressable>

      <Pressable
        onPress={() => {
          capturePress(
            pressLabel(t("homepage.review_concept.past_cycle_upload_title")),
            "upload_cycle_documents",
          );
          onUploadDocuments();
        }}
        accessibilityRole="button"
        accessibilityLabel={t(
          "homepage.review_concept.past_cycle_upload_title",
        )}
        style={({ pressed }) => ({
          ...cardSurface(),
          width: "100%",
          marginTop: 10,
          paddingHorizontal: 14,
          paddingVertical: 12,
          flexDirection: "row",
          alignItems: "center",
          gap: 12,
          opacity: pressed ? 0.86 : 1,
        })}
      >
        <OrganicMiniIcon kind="import-records" size={34} />
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text
            className="font-dm-sans"
            numberOfLines={1}
            style={{
              fontSize: 14,
              lineHeight: 18,
              color: TEXT,
              fontWeight: "800",
            }}
          >
            {t("homepage.review_concept.past_cycle_upload_title")}
          </Text>
          <CardSubtitleText numberOfLines={2}>
            {t("homepage.review_concept.past_cycle_upload_body")}
          </CardSubtitleText>
        </View>
        <Upload size={16} color={TEXT_LIGHT} strokeWidth={1.7} />
      </Pressable>
    </View>
  );
}

function CycleExportPreviewDialog({
  visible,
  onClose,
}: {
  visible: boolean;
  onClose: () => void;
}) {
  const motion = useRef(new Animated.Value(0)).current;

  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (reducedMotion) return;
    if (!visible) {
      motion.stopAnimation();
      motion.setValue(0);
      return;
    }

    const loop = Animated.loop(
      Animated.timing(motion, {
        toValue: 1,
        duration: 3600,
        easing: Easing.linear,
        useNativeDriver: true,
      }),
    );
    loop.start();
    return () => loop.stop();
  }, [motion, visible, reducedMotion]);

  const storySlide = motion.interpolate({
    inputRange: [0, 1],
    outputRange: [-18, 18],
  });
  const storyFade = motion.interpolate({
    inputRange: [0, 1],
    outputRange: [0.22, 0.7],
  });
  const cardSlide = motion.interpolate({
    inputRange: [0, 1],
    outputRange: [10, -10],
  });

  return (
    <Dialog
      visible={visible}
      onClose={onClose}
      closeButton="minimal"
      contentStyle={{
        paddingTop: 34,
        paddingHorizontal: 22,
        paddingBottom: 24,
      }}
    >
      <View style={{ alignItems: "center" }}>
        <View style={{ marginBottom: 12 }}>
          <StatusBadge type="coming-soon" />
        </View>
        <View
          style={{
            width: 48,
            height: 48,
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 12,
          }}
        >
          <OrganicMiniIcon kind="medical-records" size={46} />
        </View>
        <DialogHeader
          align="center"
          title="Cycle export"
          subtitle="Unlock a polished, physician-ready story of this cycle with outcomes, protocol, meds, monitoring, key records, and AI analysis in one secure link."
        />
      </View>

      <View
        style={{
          marginTop: 18,
          borderRadius: 20,
          backgroundColor: "rgba(255,255,255,0.58)",
          borderWidth: 1,
          borderColor: "rgba(92,52,32,0.1)",
          padding: 16,
          overflow: "hidden",
        }}
      >
        <View
          style={{
            height: 190,
            borderRadius: 18,
            backgroundColor: "rgba(255,248,236,0.52)",
            borderWidth: 1,
            borderColor: "rgba(92,52,32,0.08)",
            overflow: "hidden",
            padding: 14,
          }}
        >
          <Animated.View
            style={{
              position: "absolute",
              left: -40,
              top: 0,
              bottom: 0,
              width: 92,
              backgroundColor: "rgba(244,179,79,0.14)",
              transform: [{ translateX: storySlide }],
              opacity: storyFade,
            }}
          />

          <Animated.View
            style={{
              position: "absolute",
              left: 22,
              top: 18,
              right: 18,
              height: 62,
              borderRadius: 18,
              backgroundColor: "rgba(255,255,255,0.72)",
              borderWidth: 1,
              borderColor: "rgba(92,52,32,0.08)",
              padding: 12,
              transform: [{ translateY: cardSlide }],
            }}
          >
            <View
              style={{ flexDirection: "row", gap: 10, alignItems: "center" }}
            >
              <OrganicMiniIcon kind="medical-records" size={30} />
              <View style={{ flex: 1, gap: 6 }}>
                <View
                  style={{
                    width: "72%",
                    height: 8,
                    borderRadius: 999,
                    backgroundColor: "rgba(84,47,39,0.16)",
                  }}
                />
                <View
                  style={{
                    width: "46%",
                    height: 6,
                    borderRadius: 999,
                    backgroundColor: "rgba(183,101,69,0.18)",
                  }}
                />
              </View>
            </View>
          </Animated.View>

          <View
            style={{
              position: "absolute",
              left: 24,
              top: 94,
              right: 24,
              flexDirection: "row",
              gap: 10,
            }}
          >
            {[0, 1, 2].map((item) => (
              <View
                key={item}
                style={{
                  flex: 1,
                  height: 54,
                  borderRadius: 16,
                  backgroundColor: "rgba(255,255,255,0.64)",
                  borderWidth: 1,
                  borderColor: "rgba(92,52,32,0.08)",
                  padding: 9,
                  gap: 7,
                }}
              >
                <View
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 10,
                    backgroundColor:
                      item === 0
                        ? "rgba(244,179,79,0.34)"
                        : item === 1
                          ? "rgba(183,101,69,0.24)"
                          : "rgba(84,47,39,0.18)",
                  }}
                />
                <View
                  style={{
                    width: item === 1 ? "82%" : "64%",
                    height: 6,
                    borderRadius: 999,
                    backgroundColor: "rgba(84,47,39,0.14)",
                  }}
                />
              </View>
            ))}
          </View>

          <View
            style={{
              position: "absolute",
              left: 30,
              right: 30,
              bottom: 16,
              height: 28,
              borderRadius: 999,
              backgroundColor: "rgba(255,255,255,0.78)",
              borderWidth: 1,
              borderColor: "rgba(92,52,32,0.08)",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            <Link2 size={13} color={TEXT_MID} strokeWidth={1.8} />
            <View
              style={{
                width: 92,
                height: 6,
                borderRadius: 999,
                backgroundColor: "rgba(84,47,39,0.16)",
              }}
            />
          </View>
        </View>
      </View>

      <View style={{ marginTop: 14, gap: 8 }}>
        {[
          { icon: WandSparkles, label: "AI analysis of what shaped the cycle" },
          {
            icon: FileText,
            label: "A visual recap that tells the cycle story",
          },
          {
            icon: Send,
            label: "One secure link for physicians and care teams",
          },
        ].map((item) => {
          const Icon = item.icon;
          return (
            <View
              key={item.label}
              style={{ flexDirection: "row", alignItems: "center", gap: 9 }}
            >
              <Icon size={14} color={TEXT_MID} strokeWidth={1.8} />
              <Text
                className="font-dm-sans"
                style={{
                  flex: 1,
                  fontSize: 12,
                  lineHeight: 16,
                  color: TEXT_MID,
                  fontWeight: "700",
                }}
              >
                {item.label}
              </Text>
            </View>
          );
        })}
      </View>
    </Dialog>
  );
}

// ── Section Title ──

function PastCycleSectionTitle({
  title,
  icon,
  action,
}: {
  title: string;
  icon?: LucideIcon;
  action?: ReactNode;
}) {
  return (
    <View
      style={{
        marginTop: 28,
        marginBottom: 10,
        marginLeft: 4,
        marginRight: 2,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <SectionTitle icon={icon}>{title}</SectionTitle>
      {action}
    </View>
  );
}

function PastCycleIceSpecimenCard({
  specimen,
  width,
  onPress,
}: {
  specimen: PastCycleIceSpecimen;
  width: number;
  onPress: () => void;
}) {
  const capturePress = usePressCapture("PastCycleIceSpecimenCard");
  const { isUsed } = specimen;
  return (
    <Pressable
      onPress={() => {
        capturePress(pressLabel(specimen.label), "open_past_cycle_specimen");
        onPress();
      }}
      accessibilityRole="button"
      style={({ pressed }) => ({
        width,
        minHeight: 118,
        ...cardSurface(),
        padding: 14,
        justifyContent: "space-between",
        opacity: pressed ? 0.86 : isUsed ? 0.7 : 1,
        transform: [{ scale: pressed ? 0.995 : 1 }],
      })}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <View style={{ opacity: isUsed ? 0.45 : 1 }}>
          <OrganicMiniIcon kind={specimen.organicIcon} size={36} />
        </View>
        <ChevronRight size={19} color={TEXT_LIGHT} strokeWidth={1.7} />
      </View>

      <View style={{ gap: 5 }}>
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 7,
            flexWrap: "wrap",
          }}
        >
          <CardTitleText
            numberOfLines={1}
            style={isUsed ? { textDecorationLine: "line-through" } : undefined}
          >
            {specimen.label}
          </CardTitleText>
          {specimen.badge ? (
            <StatusBadge
              label={specimen.badge.label}
              tone={specimen.badge.tone}
            />
          ) : null}
        </View>
        <CardSubtitleText numberOfLines={1}>
          {specimen.facility
            ? `${specimen.dateLabel} · ${specimen.facility}`
            : specimen.dateLabel}
        </CardSubtitleText>
      </View>
    </Pressable>
  );
}

function PastCycleOnIceSection({
  specimens,
  onPressSpecimen,
  onAddSpecimen,
}: {
  specimens: PastCycleIceSpecimen[];
  onPressSpecimen: (id: string) => void;
  onAddSpecimen: () => void;
}) {
  const groups = ICE_SPECIMEN_TYPE_ORDER.map((type) => ({
    type,
    items: specimens.filter((specimen) => specimen.type === type),
  })).filter((group) => group.items.length > 0);

  if (groups.length === 0) {
    return (
      <DocumentRow
        title="Add specimen on ice"
        subtitle="Add frozen eggs, embryos, or sperm if this cycle created stored specimens."
        imageSource={frozenSpecimenIcon}
        trailing={
          <Plus size={18} color={EMBER.colors.textLight} strokeWidth={1.5} />
        }
        onPress={onAddSpecimen}
        showChevron={false}
      />
    );
  }

  return (
    <View style={{ gap: 16 }}>
      {groups.map((group) => (
        <View key={group.type}>
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 6,
              marginBottom: 10,
              paddingHorizontal: 2,
            }}
          >
            <Snowflake
              size={13}
              color={EMBER.colors.textLight}
              strokeWidth={1.5}
            />
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                lineHeight: 16,
                color: EMBER.colors.textLight,
                fontWeight: "800",
                letterSpacing: 0,
                textTransform: "uppercase",
              }}
            >
              {getIceSpecimenGroupLabel(group.type, group.items.length)}
            </Text>
          </View>
          <HorizontalSnapCarousel
            items={group.items}
            gap={10}
            fallbackCardWidth={156}
            getCardWidth={(containerWidth) =>
              Math.min(166, Math.max(148, containerWidth * 0.31))
            }
            keyExtractor={(item) => item.id}
            measureKey={group.items.map((item) => item.id).join("|")}
            renderItem={(item, width) => (
              <PastCycleIceSpecimenCard
                specimen={item}
                width={width}
                onPress={() => onPressSpecimen(item.id)}
              />
            )}
          />
        </View>
      ))}
    </View>
  );
}

// ── Screen ──

export default function PastCycleDetailScreen({ navigation, route }: Props) {
  const capturePress = usePressCapture("PastCycleDetailScreen");
  const { openDocumentUpload, pickAndOpenDocumentUpload } =
    useDocumentUploadController();
  const { openStartTreatment } = useStartTreatmentController();
  const t = useTranslations();
  const { showToast } = useToast();
  const showBackfillToast = usePastCycleBackfillToast();
  const { locale } = useLocale();
  const handleBack = useSmartBack({
    fallbackTab: "Care",
    fallbackScreen: "CareOverview",
  });
  const { cycleId } = route.params;
  const insets = useSafeAreaInsets();
  const queryClient = useQueryClient();
  const {
    cycle: apiCycle,
    rawCycle,
    phases,
    isLoading,
    error,
    isFetching: isFetchingCycle,
    refetch: retryCycle,
  } = usePastCycleDetail(cycleId);
  const { data: documents } = useDocuments();
  const { data: specimens } = useSpecimens();
  const { data: testResults } = useTestResults();
  const { data: careTeamMembers } = useCareTeam(cycleId);
  const { data: patientMedications = [] } = usePatientMedications();
  const { data: patientNotes = [] } = usePatientNotes();
  const { data: medicationDoseLogs = [] } = useMedicationDoseLogs({
    startDate: apiCycle?.startDate ?? undefined,
    endDate: apiCycle?.endDate ?? undefined,
    enabled: !!apiCycle?.id,
  });

  // Late-update affordance for users who recorded a positive beta (cycle
  // closed with type=pregnancy, no subtype) and now want to log delivery
  // — typically months later.
  const createPhase = useCreatePhase();
  const updatePhase = useUpdatePhase();
  const recordPregnancyStatus = useRecordCyclePregnancyStatus();
  const [updateOpen, setUpdateOpen] = useState(false);
  const updatePregnancyRequestIdRef = useRef<OutcomeWriteRequestId | null>(
    null,
  );
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [exportDialogVisible, setExportDialogVisible] = useState(false);
  const [labCryoDialogVisible, setLabCryoDialogVisible] = useState(false);
  const [labCryoFocusField, setLabCryoFocusField] = useState<
    LabCryoMetricKey | undefined
  >(undefined);
  const [addSpecimenVisible, setAddSpecimenVisible] = useState(false);
  const [addMedVisible, setAddMedVisible] = useState(false);
  const [editDetailsVisible, setEditDetailsVisible] = useState(false);
  const [editMed, setEditMed] = useState<EditMedicationData | null>(null);
  const resolveCycleNavigation = useCycleNavigation();
  const [selectedSpecimenId, setSelectedSpecimenId] = useState<string | null>(
    null,
  );
  const [labCryoSaveError, setLabCryoSaveError] = useState<string | null>(null);
  const [labCryoSaving, setLabCryoSaving] = useState(false);
  /**
   * Whether there is still a pregnancy on this cycle to say something about.
   *
   * Read from `rawCycle.outcome`, not the screen's projection: the projection
   * flattens the outcome to a string and drops `miscarriageOutcome`, so the
   * previous gate could only see a birth. It invited "Add delivery details" from
   * a cycle whose loss was already recorded.
   */
  const rawOutcome = rawCycle?.outcome ?? null;
  const pastPregnancyStatus = useCyclePregnancyStatus(cycleId);
  /** Her own words when the pregnancy ended, kept as a note on this cycle. */
  const pregnancyDetailsNote = usePregnancyDetailsNote(cycleId);
  const currentPregnancyStatus =
    pastPregnancyStatus.current === "unknown"
      ? null
      : pastPregnancyStatus.current;
  const pregnancyStatusHead =
    pastPregnancyStatus.head === "unknown" ? null : pastPregnancyStatus.head;
  /** Asked for and not in hand — still loading, or the read failed. */
  const pregnancyStatusUnknown = pastPregnancyStatus.current === "unknown";
  // Her own statement, read from the record that holds it. It used to be
  // reconstructed from the outcome's subtype fields plus the follow-up's
  // opt-in — two accounts of one fact, and neither of them hers (#3492).
  const pregnancyStatusSummary = summarisePregnancyStatus(
    currentPregnancyStatus,
  );
  /**
   * Whether there is still a pregnancy on this cycle to say something about.
   *
   * The outcome no longer carries how it concluded — recording that never
   * touches it — so what closes the door is her own last answer, and even then
   * only softly: a mis-tapped loss must stay correctable, so the affordance
   * stays open and the modal reads it as a correction.
   */
  const canUpdatePregnancy =
    !!rawOutcome?.id && rawOutcome.type === "pregnancy";

  /**
   * Save what she said, through the same write the cycle screen uses.
   *
   * A patient-owned pregnancy-status record linked to the positive outcome. It
   * does not correct that outcome — which is what let this screen offer the
   * affordance at all for a result her clinic recorded.
   */
  const handleSavePregnancyStatus = useCallback(
    async (status: PregnancyStatus, details: string | null) => {
      if (!rawCycle?.id || !rawOutcome?.id || pregnancyStatusUnknown) return;
      setUpdateError(null);
      try {
        // Her words first, as on the cycle screen: a failure saves nothing,
        // and a retry finds the note already there or replays it.
        if (details !== null) {
          await pregnancyDetailsNote.save(details, toApiDate(new Date()));
        }
        const requestId = resolveOutcomeWriteRequestId(
          updatePregnancyRequestIdRef.current,
          pregnancyStatusWriteTarget(pregnancyStatusHead),
          createIdempotencyKey,
        );
        updatePregnancyRequestIdRef.current = requestId;
        await recordPregnancyStatus.mutateAsync({
          cycleId: rawCycle.id,
          body: buildPatientPregnancyStatusWrite({
            status,
            outcomeId: rawOutcome.id,
            clientRequestId: requestId.requestId,
            current: pregnancyStatusHead,
            today: toApiDate(new Date()),
          }),
        });
        updatePregnancyRequestIdRef.current = null;
        setUpdateOpen(false);
      } catch (e) {
        // See the cycle screen: a conflict is aimed at a different record next
        // time, so it needs a new request id; every other failure keeps the id
        // so a retry after a lost response replays rather than writes twice.
        if (e instanceof CyclePregnancyStatusWriteError && e.isConflict) {
          updatePregnancyRequestIdRef.current = null;
        }
        // The shared mapper, never a raw API string.
        setUpdateError(pregnancyStatusErrorMessage(e, t));
      }
    },
    [
      pregnancyDetailsNote,
      pregnancyStatusHead,
      pregnancyStatusUnknown,
      rawCycle?.id,
      rawOutcome?.id,
      recordPregnancyStatus,
      t,
    ],
  );

  const handleSaveLabCryoMetrics = useCallback(
    async (values: LabCryoMetricValues) => {
      if (!apiCycle?.id) return;

      const phaseTypeForField = (key: LabCryoMetricKey) => {
        if (key === "blastocysts" && apiCycle.cycleTypeRaw === "egg_freezing") {
          return "egg_retrieval";
        }
        return LAB_CRYO_METRIC_FIELDS.find((field) => field.key === key)!
          .defaultPhaseType;
      };

      const today = toApiDate(new Date());
      const fallbackStartDate = apiCycle.startDate ?? today;
      const grouped = new Map<
        string,
        { key: LabCryoMetricKey; metricType: string; value: number | null }[]
      >();

      for (const field of LAB_CRYO_METRIC_FIELDS) {
        const phaseType = phaseTypeForField(field.key);
        const group = grouped.get(phaseType) ?? [];
        group.push({
          key: field.key,
          metricType: field.metricType,
          value: values[field.key],
        });
        grouped.set(phaseType, group);
      }

      try {
        setLabCryoSaving(true);
        setLabCryoSaveError(null);

        for (const [phaseType, metrics] of grouped) {
          const existingPhase =
            phases.find((phase) => phase.type === phaseType) ?? null;
          const targetTypes = new Set(
            metrics.map((metric) => metric.metricType),
          );
          const preservedMetrics = (existingPhase?.fertilityMetrics ?? [])
            .filter((metric) => metric.type && !targetTypes.has(metric.type))
            .map((metric) => ({
              ...(metric.id ? { id: metric.id } : {}),
              type: metric.type!,
              value: (metric.value as number | null) ?? null,
            }));
          const targetMetrics = metrics
            .map((metric) => {
              const existingMetric = existingPhase?.fertilityMetrics?.find(
                (item) => item.type === metric.metricType,
              );
              if (metric.value == null && !existingMetric?.id) return null;
              return {
                ...(existingMetric?.id ? { id: existingMetric.id } : {}),
                type: metric.metricType,
                value: metric.value,
              };
            })
            .filter(
              (
                metric,
              ): metric is {
                id?: string;
                type: string;
                value: number | null;
              } => metric !== null,
            );

          if (existingPhase?.id) {
            await updatePhase.mutateAsync({
              cycleId: apiCycle.id,
              phaseId: existingPhase.id,
              body: {
                fertilityMetrics: [...preservedMetrics, ...targetMetrics],
              },
            });
          } else {
            const metricsToCreate = targetMetrics.filter(
              (metric) => metric.value != null,
            );
            if (metricsToCreate.length === 0) continue;
            await createPhase.mutateAsync({
              cycleId: apiCycle.id,
              body: {
                type: phaseType,
                startDate: fallbackStartDate,
                ...(apiCycle.endDate ? { endDate: apiCycle.endDate } : {}),
                fertilityMetrics: metricsToCreate,
              },
            });
          }
        }

        await queryClient.refetchQueries({
          queryKey: ["cycle-phases"],
          type: "all",
        });
        setLabCryoDialogVisible(false);
      } catch (saveError) {
        setLabCryoSaveError(
          saveError instanceof Error
            ? saveError.message
            : "Unable to save lab and cryo values.",
        );
      } finally {
        setLabCryoSaving(false);
      }
    },
    [apiCycle, createPhase, phases, queryClient, updatePhase],
  );

  const cycle = useMemo<PastCycle | null>(() => {
    if (!apiCycle || !rawCycle) return null;

    const observedResults = extractObservedResults(phases);
    const finalPreservationResultRecorded =
      (rawCycle.cycleType === "egg_freezing" ||
        rawCycle.cycleType === "embryo_freezing") &&
      observedResults.some((result) => result.metric === "frozen_embryo");
    const lifecycle = resolvePostCycleLifecycle(
      {
        status: rawCycle.status,
        cycleType: rawCycle.cycleType,
        outcome: rawCycle.outcome ?? null,
      },
      observedResults.length,
      finalPreservationResultRecorded,
    );
    const outcome: CycleOutcome = (() => {
      if (lifecycle.state === "cancelled") return "cancelled";
      if (lifecycle.clinicalState === "pregnancy") return "positive";
      if (
        lifecycle.clinicalState === "no_pregnancy" ||
        lifecycle.clinicalState === "adverse"
      ) {
        return "negative";
      }
      if (
        lifecycle.state === "in_progress" ||
        lifecycle.state === "results_recorded_open"
      ) {
        return "ongoing";
      }
      if (lifecycle.state === "completed_preservation") return "frozen";
      return "closed";
    })();

    const funnelMap = Object.fromEntries(
      apiCycle.resultsFunnel.map((r) => [r.label, r.value]),
    );

    const medications =
      apiCycle.medications.length > 0
        ? apiCycle.medications.map((m) => `${m.name} ${m.dosage}`.trim())
        : null;

    const stimSheet =
      apiCycle.stimSheet.length > 0
        ? {
            rows: [
              { label: "E2 (pg/mL)", key: "e2" },
              { label: "LH (mIU/mL)", key: "lh" },
              { label: "P4 (ng/mL)", key: "p4" },
              { label: "Follicles", key: "follicles" },
              { label: "Lining (mm)", key: "lining" },
            ].filter((row) =>
              apiCycle.stimSheet.some(
                (d) => (d as Record<string, unknown>)[row.key] !== undefined,
              ),
            ),
            data: apiCycle.stimSheet.map((d) => ({
              day: `D${d.day}`,
              date: d.date ? formatShortDate(d.date, locale) : "",
              ...(d.e2 !== undefined && { e2: String(d.e2) }),
              ...(d.lh !== undefined && { lh: String(d.lh) }),
              ...(d.p4 !== undefined && { p4: String(d.p4) }),
              ...(d.follicles !== undefined && {
                follicles: String(d.follicles),
              }),
              ...(d.lining !== undefined && { lining: String(d.lining) }),
            })),
          }
        : null;

    const journeyPhases: JourneyPhase[] | null =
      apiCycle.journeyPhases.length > 0
        ? apiCycle.journeyPhases.map((p) => ({
            label: p.label,
            icon: PHASE_ICONS[p.type] ?? ClipboardCheck,
            timing: p.startDate
              ? p.endDate
                ? `${formatShortDate(p.startDate, locale)} – ${formatShortDate(p.endDate, locale)}`
                : formatShortDate(p.startDate, locale)
              : "—",
            description: p.isCompleted ? "Completed" : "Not started",
          }))
        : null;

    const summaryParts = apiCycle.resultsFunnel.map(
      (r) => `${r.value} ${r.label.toLowerCase()}`,
    );
    const outcomeSummary =
      apiCycle.outcomeNotes ||
      summaryParts.join(", ") ||
      apiCycle.description ||
      "";

    return {
      id: apiCycle.id,
      cycleType: apiCycle.cycleType,
      cycleTypeRaw: apiCycle.cycleTypeRaw,
      cycleNumber: apiCycle.cycleNumber,
      startDate: formatMonthYear(apiCycle.startDate, locale),
      endDate: apiCycle.endDate
        ? formatMonthYear(apiCycle.endDate, locale)
        : undefined,
      clinicName: apiCycle.clinic ?? "—",
      doctorName: null,
      outcome,
      outcomeSummary,
      cancellationReason: apiCycle.cancellationReason,
      outcomeNotes: apiCycle.outcomeNotes,
      protocol: apiCycle.protocol,
      medications,
      eggsRetrieved: funnelMap["Eggs Retrieved"] ?? null,
      eggsMature: funnelMap["Mature Eggs (M2)"] ?? null,
      fertilized: funnelMap["Fertilized"] ?? null,
      embryosDay3: funnelMap["Day 3 embryos"] ?? null,
      blastocystCount: funnelMap["Blastocysts"] ?? null,
      blastocysts: funnelMap["Embryos Frozen"] ?? null,
      pgtTested: null,
      euploid: null,
      embryosTransferred: funnelMap["Embryos Transferred"] ?? null,
      liningAtTransfer: null,
      notes: null,
      stimSheet,
      protocolChanges: null,
      journeyPhases,
    };
  }, [apiCycle, locale, phases, rawCycle]);

  const pastProtocolData = useMemo<CycleProtocolData>(() => {
    if (!cycle || !apiCycle) {
      return { id: cycleId, medications: [], medicationPeriods: [], notes: [] };
    }

    const phaseMedicationIds = new Set(
      phases.flatMap((phase) =>
        (phase.patientMedications ?? []).map((medication) => medication.id),
      ),
    );
    const medicationRowsByKey = new Map<string, CycleProtocolMedication>();
    const medicationPeriods: CycleProtocolMedicationPeriod[] = [];
    const startDate = apiCycle.startDate;
    const endDate = apiCycle.endDate;

    const toForm = (value: string | null | undefined, route: string) => {
      const normalized = `${value ?? ""} ${route}`.toLowerCase();
      if (normalized.includes("pen")) return "pen" as MedForm;
      if (
        normalized.includes("vial") ||
        normalized.includes("injection") ||
        normalized.includes("syringe") ||
        normalized.includes("subcutaneous") ||
        normalized.includes("intramuscular")
      ) {
        return "vial" as MedForm;
      }
      if (normalized.includes("supp") || normalized.includes("vaginal")) {
        return "suppository" as MedForm;
      }
      return "pill" as MedForm;
    };

    const toDayLabel = (date: string | null | undefined) => {
      if (!date || !startDate) return undefined;
      const start = new Date(`${startDate}T12:00:00`).getTime();
      const current = new Date(`${date}T12:00:00`).getTime();
      if (!Number.isFinite(start) || !Number.isFinite(current)) {
        return undefined;
      }
      const day = Math.floor((current - start) / 86400000) + 1;
      return t("care.cycle_detail.timeline.day_label", {
        params: { day: String(day) },
      });
    };

    const rawMedications = patientMedications.filter((medication) => {
      const attachedToCycle =
        medication.cycleId === cycle.id ||
        phaseMedicationIds.has(medication.id);
      if (!attachedToCycle) return false;
      if (startDate && medication.endDate && medication.endDate < startDate) {
        return false;
      }
      if (endDate && medication.startDate && medication.startDate > endDate) {
        return false;
      }
      return true;
    });

    for (const medication of rawMedications) {
      const name =
        medication.medication?.name ??
        medication.rawMedicationName ??
        t("care.cycle_protocol.labels.default_medication_name");
      const dose =
        medication.dose == null
          ? ""
          : `${medication.dose}${medication.doseUnit ? ` ${medication.doseUnit}` : ""}`;
      const times = Array.isArray(medication.timeTaken)
        ? medication.timeTaken.filter(
            (time): time is string => typeof time === "string",
          )
        : [];
      const route = medication.route ?? "";
      const form = toForm(medication.form, route);
      // An undated course stays in the medication list but has no place on
      // the cycle timeline.
      const periods: CycleProtocolMedicationPeriod[] = medication.startDate
        ? [
            {
              id: medication.id,
              name,
              doseLabel: dose || null,
              form,
              route,
              startDate: medication.startDate,
              endDate: medication.endDate ?? null,
              timeTaken: times,
              notes: medication.notes ?? null,
              stopped: /^stopped(?:\s|:|$)/i.test(medication.notes ?? ""),
            },
          ]
        : [];
      medicationPeriods.push(...periods);

      // An undated course keeps its own row: merged into a dated row it would
      // have no timeline period and no way to open it for editing.
      const key = medication.startDate
        ? `${name.trim().toLowerCase()}-${form}-${route}`
        : `undated-${medication.id}`;
      const existing = medicationRowsByKey.get(key);
      const historyEntry = dose
        ? {
            cycle: `${cycle.cycleType} #${cycle.cycleNumber}`,
            dosage: dose,
            note: times.length ? times.join(", ") : undefined,
          }
        : undefined;
      if (existing) {
        const badges = existing.dosageBadges ?? [];
        if (dose && !badges.includes(dose)) badges.push(dose);
        existing.dosageBadges = badges;
        existing.dosage = badges[0] ?? existing.dosage;
        existing.periods = [...(existing.periods ?? []), ...periods];
        existing.userContext = {
          ...existing.userContext,
          dosage: badges.join(", "),
          timeTaken: Array.from(
            new Set([...(existing.userContext?.timeTaken ?? []), ...times]),
          ),
          startDate:
            medication.startDate &&
            (!existing.userContext?.startDate ||
              medication.startDate < existing.userContext.startDate)
              ? medication.startDate
              : existing.userContext?.startDate,
          endDate:
            existing.userContext?.endDate === null ||
            medication.endDate === null
              ? null
              : (medication.endDate ?? existing.userContext?.endDate),
          history: historyEntry
            ? [...(existing.userContext?.history ?? []), historyEntry]
            : existing.userContext?.history,
        };
        continue;
      }

      medicationRowsByKey.set(key, {
        type: "medication",
        id: medication.id,
        name,
        brandName: name,
        dosage: dose,
        dosageBadges: dose ? [dose] : [],
        form,
        route,
        time: times.join(", "),
        timing:
          times[0]?.startsWith("0") || Number(times[0]?.slice(0, 2)) < 12
            ? "morning"
            : "evening",
        color: semanticColors.accent.default,
        recurrence: deriveRecurrence({
          startDate: medication.startDate,
          endDate: medication.endDate,
        }),
        sinceDay: toDayLabel(medication.startDate),
        untilDay: toDayLabel(medication.endDate),
        userContext: {
          dosage: dose,
          route,
          selectedForm: medication.form ?? undefined,
          timeTaken: times,
          startDate: medication.startDate ?? undefined,
          endDate: medication.endDate ?? null,
          notes: medication.notes ?? undefined,
          history: historyEntry ? [historyEntry] : undefined,
        },
        periods,
      });
    }

    const medicationIds = new Set(medicationPeriods.map((period) => period.id));
    const doseLogs: CycleProtocolDoseLog[] = medicationDoseLogs
      .filter((log) => medicationIds.has(log.medicationId))
      .map((log) => ({
        medicationId: log.medicationId,
        scheduledDate: log.scheduledDate,
        scheduledTime: log.scheduledTime,
        status: log.status === "taken_late" ? "late" : log.status,
      }))
      .filter(
        (log): log is CycleProtocolDoseLog =>
          log.status === "taken" ||
          log.status === "late" ||
          log.status === "missed",
      );

    const notes = patientNotes
      .filter((note) => {
        if (!note.date) return false;
        // Her words about a pregnancy that ended are not timeline context.
        if (isPregnancyDetailsNote(note.text)) return false;
        const legacy = note.text?.includes("__protocol_adjustment__");
        const withinCycle =
          (!startDate || note.date >= startDate) &&
          (!endDate || note.date <= endDate);
        return withinCycle && (note.cycleId === cycle.id || legacy);
      })
      .map((note) => ({
        id: note.id,
        date: note.date!,
        text: note.text ?? "",
      }));

    return {
      id: cycle.id,
      startDate,
      endDate,
      medications: Array.from(medicationRowsByKey.values()),
      medicationPeriods,
      doseLogs,
      notes,
    };
  }, [
    apiCycle,
    cycle,
    cycleId,
    medicationDoseLogs,
    patientMedications,
    patientNotes,
    phases,
    t,
  ]);

  const handleEditMedication = useCallback(
    (
      medication: MedicationScheduleItem,
      period?: CycleProtocolMedicationPeriod,
    ) => {
      const groupedMedication = medication as CycleProtocolMedication;
      const latestPeriod = [...(groupedMedication.periods ?? [])].sort((a, b) =>
        b.startDate.localeCompare(a.startDate),
      )[0];
      const raw = patientMedications.find(
        (item) => item.id === (period?.id ?? latestPeriod?.id ?? medication.id),
      );
      if (!raw) return;
      setEditMed({
        id: raw.id,
        name: raw.medication?.name ?? raw.rawMedicationName ?? medication.name,
        dose: raw.dose ?? null,
        doseUnit: raw.doseUnit ?? null,
        route: raw.route ?? null,
        form: raw.form ?? null,
        timeTaken: Array.isArray(raw.timeTaken) ? raw.timeTaken : [],
        startDate: raw.startDate ?? undefined,
        endDate: raw.endDate ?? null,
        notes: raw.notes ?? null,
      });
    },
    [patientMedications],
  );

  const cycleDocuments = useMemo(
    () => getCycleDocuments(documents, cycle?.id),
    [documents, cycle],
  );
  const cycleIceSpecimens = useMemo(
    () => buildPastCycleIceSpecimens(specimens, cycle, locale, t),
    [specimens, cycle, locale, t],
  );
  const cryoSpecimenDetails = useMemo(
    () =>
      mapApiSpecimensToCryoDetails(specimens ?? [], (apiType, index) =>
        buildCryoSpecimenLabel(t, apiType, index),
      ),
    [specimens, t],
  );
  const cycleTestResults = useMemo(
    () =>
      cycle
        ? (testResults ?? []).filter((result) => result.cycleId === cycle.id)
        : [],
    [testResults, cycle],
  );
  const cycleTeamMembers = useMemo(
    () =>
      cycle
        ? buildCycleTeamMembers({
            members: careTeamMembers,
            clinicName: cycle.clinicName,
            doctorName: cycle.doctorName,
          })
        : [],
    [careTeamMembers, cycle],
  );
  const cycleClinicName = getKnownCycleValue(cycle?.clinicName);

  const outcomeChip = useMemo<{ label: string } | null>(() => {
    if (!cycle) {
      return null;
    }
    const o = cycle.outcome;
    const cancellation = cycle.cancellationReason;
    const pregOutcome = apiCycle?.pregnancyOutcome ?? null;
    const eggs = cycle.eggsRetrieved ?? null;
    const embryos = cycle.blastocysts ?? null;
    const isPreservation =
      cycle.cycleTypeRaw === "egg_freezing" ||
      cycle.cycleTypeRaw === "embryo_freezing";

    if (o === "cancelled") {
      if (cancellation && cancellation in CANCELLATION_REASON_KEYS) {
        return {
          label: t("care.past_cycle_detail.outcome_cancelled_with_reason", {
            params: {
              reason: t(
                CANCELLATION_REASON_KEYS[cancellation as CancellationReason],
              ),
            },
          }),
        };
      }
      return { label: t("care.past_cycle_detail.outcome_cancelled") };
    }
    if (o === "positive") {
      if (pregOutcome === "live_birth") {
        return { label: t("care.past_cycle_detail.outcome_live_birth") };
      }
      if (pregOutcome === "multiple_live_birth") {
        return {
          label: t("care.past_cycle_detail.outcome_multiple_live_birth"),
        };
      }
      return {
        label: t("care.past_cycle_detail.outcome_positive_beta"),
      };
    }
    if (o === "negative") {
      return { label: t("care.past_cycle_detail.outcome_negative") };
    }
    if (o === "completed") {
      if (cycle.cycleTypeRaw === "egg_freezing" && embryos != null) {
        return {
          label: t("care.past_cycle_detail.eggs_frozen", {
            params: { count: String(embryos) },
          }),
        };
      }
      if (cycle.cycleTypeRaw === "egg_freezing" && eggs != null) {
        return {
          label: t("care.past_cycle_detail.eggs_retrieved", {
            params: { count: String(eggs) },
          }),
        };
      }
      if (isPreservation && embryos != null) {
        return {
          label: t("care.past_cycle_detail.embryos_frozen", {
            params: { count: String(embryos) },
          }),
        };
      }
      return { label: t("care.past_cycle_detail.outcome_completed") };
    }
    if (o === "frozen") {
      if (embryos != null) {
        return {
          label: t(
            cycle.cycleTypeRaw === "egg_freezing"
              ? "care.past_cycle_detail.eggs_frozen"
              : "care.past_cycle_detail.embryos_frozen",
            {
              params: { count: String(embryos) },
            },
          ),
        };
      }
      if (cycle.cycleTypeRaw === "egg_freezing" && eggs != null) {
        return {
          label: t("care.past_cycle_detail.eggs_retrieved", {
            params: { count: String(eggs) },
          }),
        };
      }
      if (isPreservation) return null;
      return { label: t("care.past_cycle_detail.outcome_frozen") };
    }
    if (o === "ongoing") {
      return { label: t("care.past_cycle_detail.outcome_ongoing") };
    }
    return null;
  }, [cycle, apiCycle, t]);

  if (isLoading) {
    return (
      <GlassScreenLayout>
        <View
          style={{
            flex: 1,
            alignItems: "center",
            justifyContent: "center",
            paddingTop: SCREEN_PADDING_TOP + 100,
          }}
          testID="screen-loading"
        >
          <ActivityIndicator size="large" color={EMBER.colors.warm} />
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 14,
              color: EMBER.colors.textLight,
              marginTop: 16,
            }}
          >
            {t("care.past_cycle_detail.loading")}
          </Text>
        </View>
      </GlassScreenLayout>
    );
  }

  // The full failure only when nothing loaded. A failed refresh keeps the
  // cycle on screen with a stale row below the hero.
  if (error && !cycle) {
    return (
      <GlassScreenLayout>
        <View
          style={{
            flex: 1,
            justifyContent: "center",
            paddingTop: SCREEN_PADDING_TOP + 100,
            paddingHorizontal: 20,
            maxWidth: 520,
            width: "100%",
            alignSelf: "center",
          }}
        >
          <ClinicalDataStatus
            state="unavailable"
            title={t("care.past_cycle_detail.error_title")}
            message={t("clinical_data.unavailable_body")}
            retryLabel={t("clinical_data.retry")}
            retryingLabel={t("clinical_data.retrying")}
            onRetry={retryCycle}
            isRetrying={isFetchingCycle}
            testID="past-cycle-detail-unavailable"
          />
          <CompactActionButton
            label={t("care.past_cycle_detail.go_back")}
            action="care.past_cycle_detail.go_back"
            onPress={() => handleBack()}
            variant="secondary"
            style={{ marginTop: 16, alignSelf: "center" }}
          />
        </View>
      </GlassScreenLayout>
    );
  }

  if (!cycle) {
    return (
      <GlassScreenLayout>
        <View
          style={{
            flex: 1,
            alignItems: "center",
            justifyContent: "center",
            paddingTop: SCREEN_PADDING_TOP + 100,
            paddingHorizontal: 40,
          }}
        >
          <AlertTriangle
            size={32}
            color={EMBER.colors.warm}
            strokeWidth={1.5}
          />
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 16,
              fontWeight: "600",
              color: EMBER.colors.text,
              marginTop: 16,
              textAlign: "center",
            }}
          >
            {t("care.past_cycle_detail.error_title")}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 14,
              color: EMBER.colors.textLight,
              marginTop: 8,
              textAlign: "center",
            }}
          >
            {t("care.past_cycle_detail.error_body")}
          </Text>
          <CompactActionButton
            label={t("care.past_cycle_detail.go_back")}
            action="care.past_cycle_detail.go_back"
            onPress={() => handleBack()}
            variant="secondary"
            style={{ marginTop: 24 }}
          />
        </View>
      </GlassScreenLayout>
    );
  }

  const isIVF = isIvfLikeCycleType(cycle.cycleTypeRaw, cycle.cycleType);
  const isMonitoredCycle = isMonitoredCycleType(
    cycle.cycleTypeRaw,
    cycle.cycleType,
  );
  const completionItems = getCycleCompletionItems(cycle, isIVF);
  const missingCompletionItems = completionItems.filter(
    (item) => !item.complete,
  );
  const completedCompletionItemCount =
    completionItems.length - missingCompletionItems.length;
  const cycleDocumentFilter = getCycleDocumentTag(cycle.id);
  const cycleDocumentFilterLabel = getCycleDocumentFilterLabel({
    cycleType: cycle.cycleType,
    cycleNumber: cycle.cycleNumber,
  });
  const showOnIceSection = cycleIceSpecimens.length > 0;
  const outcomeUploadCopy = getOutcomeUploadCopy(cycle);
  const openOutcomeRecords = (stageKey?: string) => {
    if (stageKey === "pregnancy-outcome" && canUpdatePregnancy) {
      setUpdateError(null);
      setUpdateOpen(true);
      return;
    }
    if (isIVF) {
      setLabCryoFocusField(resolveLabCryoFieldForStage(stageKey));
      setLabCryoDialogVisible(true);
      return;
    }
    showBackfillToast();
  };
  const openCycleDocuments = (openUpload = false, initialCategory?: string) => {
    if (openUpload) {
      openDocumentUpload({
        defaultCategory: initialCategory ?? "medical_records",
        defaultTags: [cycleDocumentFilter],
      });
      return;
    }

    navigation.navigate("Documents", {
      initialCategory,
      initialDocumentFilter: cycleDocumentFilter,
      initialDocumentFilterLabel: cycleDocumentFilterLabel,
      openUpload,
    });
  };
  const openCycleSpecimenCapture = () => {
    setAddSpecimenVisible(true);
  };

  return (
    <>
      <GlassScreenLayout edges={["bottom", "left", "right"]}>
        <ScrollView
          contentContainerStyle={{
            paddingBottom: 100,
            maxWidth: 520,
            width: "100%",
            alignSelf: "center",
          }}
          showsVerticalScrollIndicator={false}
        >
          {/* ── HERO HEADER ── */}
          <View
            style={{
              paddingTop: insets.top + 12,
              paddingHorizontal: 20,
              paddingBottom: 28,
              borderBottomLeftRadius: 28,
              borderBottomRightRadius: 28,
              overflow: "hidden",
            }}
          >
            {/* Bronze-gradient background */}
            <Image
              source={require("../../assets/images/bronze-gradient.webp")}
              style={[
                StyleSheet.absoluteFill,
                { width: "100%", height: "100%" },
              ]}
              resizeMode="cover"
            />

            {/* Decorative illustration — top-right (muted for closed cycle) */}
            <View
              pointerEvents="none"
              style={{
                position: "absolute",
                top: insets.top + 16,
                right: 0,
              }}
            >
              <CycleIllustration cycleType={cycle.cycleTypeRaw} muted />
            </View>

            {/* Back */}
            <Pressable
              onPress={() => {
                capturePress(
                  pressLabel("back_from_past_cycle"),
                  "back_from_past_cycle",
                );
                handleBack();
              }}
              style={{
                width: 36,
                height: 36,
                borderRadius: 18,
                backgroundColor: semanticColors.onDark.fill.subtle,
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 20,
              }}
            >
              <ArrowLeft
                size={18}
                color={semanticColors.onDark.text.secondary}
              />
            </Pressable>

            {/* Eyebrow */}
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 6,
                alignSelf: "flex-start",
                marginBottom: 6,
              }}
            >
              <History
                size={13}
                color={semanticColors.onDark.text.tertiary}
                strokeWidth={1.5}
              />
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 11,
                  fontWeight: "700",
                  color: semanticColors.onDark.text.tertiary,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                }}
              >
                {t("care.past_cycle_detail.header_eyebrow")}
              </Text>
            </View>

            {/* Title row */}
            <View
              style={{ flexDirection: "row", alignItems: "flex-start", gap: 8 }}
            >
              <Text
                className="font-dm-sans"
                style={{
                  flex: 1,
                  fontSize: 22,
                  fontWeight: "700",
                  color: semanticColors.onDark.text.primary,
                  letterSpacing: -0.3,
                  lineHeight: 27,
                }}
              >
                {cycle.cycleType} #{cycle.cycleNumber}
              </Text>
            </View>

            {/* Metadata chips */}
            <View
              style={{
                flexDirection: "row",
                flexWrap: "wrap",
                gap: 8,
                marginTop: 16,
              }}
            >
              {outcomeChip ? (
                <View
                  style={{
                    backgroundColor: semanticColors.onDark.fill.strong,
                    paddingHorizontal: 10,
                    paddingVertical: 5,
                    borderRadius: 8,
                  }}
                >
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 12,
                      fontWeight: "600",
                      color: semanticColors.onDark.text.secondary,
                    }}
                  >
                    {outcomeChip.label}
                  </Text>
                </View>
              ) : (
                <Pressable
                  onPress={() => {
                    capturePress(
                      pressLabel("Add outcome"),
                      "add_cycle_outcome",
                    );
                    showToast({
                      variant: "info",
                      message: t(
                        "care.past_cycle_detail.add_outcome_unavailable",
                      ),
                    });
                  }}
                  style={({ pressed }) => ({
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 4,
                    backgroundColor: pressed
                      ? semanticColors.onDark.fill.subtle
                      : "transparent",
                    paddingHorizontal: 10,
                    paddingVertical: 5,
                    borderRadius: 8,
                    borderWidth: 1,
                    borderStyle: "dashed",
                    borderColor: semanticColors.onDark.fill.strong,
                  })}
                >
                  <Plus
                    size={11}
                    color={semanticColors.onDark.text.secondary}
                    strokeWidth={2}
                  />
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 12,
                      fontWeight: "500",
                      color: semanticColors.onDark.text.secondary,
                    }}
                  >
                    Add outcome
                  </Text>
                </Pressable>
              )}
              {cycle.clinicName && cycle.clinicName !== "—" ? (
                <Tag label={cycle.clinicName} variant="outline" tone="onDark" />
              ) : (
                <Pressable
                  onPress={() => {
                    capturePress(pressLabel("Add clinic"), "add_cycle_clinic");
                    setEditDetailsVisible(true);
                  }}
                  style={({ pressed }) => ({
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 4,
                    backgroundColor: pressed
                      ? semanticColors.onDark.fill.subtle
                      : "transparent",
                    paddingHorizontal: 10,
                    paddingVertical: 5,
                    borderRadius: 8,
                    borderWidth: 1,
                    borderStyle: "dashed",
                    borderColor: semanticColors.onDark.fill.strong,
                  })}
                >
                  <Plus
                    size={11}
                    color={semanticColors.onDark.text.secondary}
                    strokeWidth={2}
                  />
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 12,
                      fontWeight: "500",
                      color: semanticColors.onDark.text.secondary,
                    }}
                  >
                    {t("care.past_cycle_detail.add_clinic")}
                  </Text>
                </Pressable>
              )}
              {cycle.startDate && cycle.startDate !== "—" ? (
                <Tag
                  label={`${cycle.startDate}${
                    cycle.endDate && cycle.endDate !== "—"
                      ? ` – ${cycle.endDate}`
                      : ""
                  }`}
                  variant="outline"
                  tone="onDark"
                />
              ) : (
                <Pressable
                  onPress={() => {
                    capturePress(pressLabel("Add dates"), "add_cycle_dates");
                    setEditDetailsVisible(true);
                  }}
                  style={({ pressed }) => ({
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 4,
                    backgroundColor: pressed
                      ? semanticColors.onDark.fill.subtle
                      : "transparent",
                    paddingHorizontal: 10,
                    paddingVertical: 5,
                    borderRadius: 8,
                    borderWidth: 1,
                    borderStyle: "dashed",
                    borderColor: semanticColors.onDark.fill.strong,
                  })}
                >
                  <Plus
                    size={11}
                    color={semanticColors.onDark.text.secondary}
                    strokeWidth={2}
                  />
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 12,
                      fontWeight: "500",
                      color: semanticColors.onDark.text.secondary,
                    }}
                  >
                    {t("care.past_cycle_detail.add_dates")}
                  </Text>
                </Pressable>
              )}
            </View>
          </View>

          {/* ── BODY ── */}
          <View style={{ paddingHorizontal: 16, paddingTop: 8 }}>
            {error ? (
              <ClinicalDataStatus
                state="stale"
                message={t("clinical_data.stale_body")}
                retryLabel={t("clinical_data.retry")}
                retryingLabel={t("clinical_data.retrying")}
                onRetry={retryCycle}
                isRetrying={isFetchingCycle}
                style={{ marginTop: 8 }}
                testID="past-cycle-detail-stale"
              />
            ) : null}
            <CycleCompletionCard
              completed={completedCompletionItemCount}
              total={completionItems.length}
              missing={missingCompletionItems}
              onOpenExport={() => setExportDialogVisible(true)}
              onUploadDocuments={() =>
                openCycleDocuments(true, "medical_records")
              }
            />

            {/* Summary / Notes */}
            {cycle.outcomeSummary ? (
              <>
                <PastCycleSectionTitle
                  title={
                    cycle.outcome === "cancelled" && cycle.outcomeNotes
                      ? "Cancellation notes"
                      : "Cycle recap"
                  }
                />
                <View style={{ ...cardStyle, padding: 20 }}>
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 15,
                      color: EMBER.colors.textMid,
                      lineHeight: 22,
                    }}
                  >
                    {cycle.outcomeSummary}
                  </Text>
                </View>
              </>
            ) : null}

            {/* Update outcome — visible only on positive cycles that don't
              yet have a pregnancyOutcome (i.e. user recorded the beta but
              hasn't logged delivery details). Quietly invites them to do so
              when they're ready. */}
            {canUpdatePregnancy ? (
              <>
                <PastCycleSectionTitle
                  title={t("care.update_pregnancy.where_things_stand")}
                  icon={HeartHandshake}
                />
                <View
                  style={{
                    ...cardStyle,
                    paddingTop: 24,
                    paddingBottom: 20,
                    paddingHorizontal: 20,
                    gap: 14,
                  }}
                >
                  {/*
                    What she last told us, when she has told us anything.
                    Saving "still pregnant" changes nothing else on this page —
                    correctly, nothing has ended — but with no trace of the answer
                    the save read as having been ignored. Same line and same strings
                    the cycle page shows, so the two cannot describe one record
                    differently.
                  */}
                  {pregnancyStatusSummary ? (
                    <View style={{ gap: 2 }}>
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 14,
                          lineHeight: 20,
                          color: EMBER.colors.text,
                        }}
                      >
                        {t(
                          `care.update_pregnancy.stands_${pregnancyStatusSummary.stands}`,
                        )}
                      </Text>
                      {pregnancyStatusSummary.weekCount !== null ? (
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 12.5,
                            lineHeight: 18,
                            color: EMBER.colors.textLight,
                          }}
                        >
                          {t(
                            pregnancyStatusSummary.weekCount === 1
                              ? "care.update_pregnancy.weeks_along_one"
                              : "care.update_pregnancy.weeks_along_other",
                            {
                              params: {
                                count: String(pregnancyStatusSummary.weekCount),
                              },
                            },
                          )}
                        </Text>
                      ) : null}
                    </View>
                  ) : pregnancyStatusUnknown ? null : (
                    // Only once her record has actually come back. Loading and a
                    // failed read both leave the summary null, and inviting
                    // someone to "say where things stand" about a loss she
                    // already recorded is the invitation this page must not make.
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 14,
                        color: EMBER.colors.textMid,
                        lineHeight: 20,
                      }}
                    >
                      {t("care.update_pregnancy.say_where_things_stand")}
                    </Text>
                  )}
                  <CompactActionButton
                    testID="update-pregnancy-trigger"
                    variant="secondary"
                    label={t("care.update_pregnancy.specify_the_outcome")}
                    onPress={() => {
                      setUpdateError(null);
                      setUpdateOpen(true);
                    }}
                  />
                  {/* The dialog shows the error where she is looking when the save
                      fails; repeating it on the card behind left a red line sitting
                      under the button after she dismissed it. */}
                </View>
              </>
            ) : null}

            <PastCycleSectionTitle
              title="Outcomes"
              icon={Beaker}
              action={
                isIVF ? (
                  <CompactActionButton
                    label={t(
                      "care.past_cycle_detail.lab_cryo_dialog.fill_in_all",
                    )}
                    action="open_outcome_records"
                    onPress={() => openOutcomeRecords()}
                    variant="secondary"
                    size="sm"
                    leadingIcon={ClipboardCheck}
                    hitSlop={8}
                  />
                ) : undefined
              }
            />
            <View style={{ gap: 9 }}>
              <PastCycleOutcomesFunnel
                cycle={cycle}
                onAddRecords={openOutcomeRecords}
              />
              <DocumentRow
                title={outcomeUploadCopy.title}
                subtitle={outcomeUploadCopy.subtitle}
                icon={<OrganicMiniIcon kind="import-records" size={34} />}
                trailing={
                  <Upload
                    size={18}
                    color={EMBER.colors.textLight}
                    strokeWidth={1.5}
                  />
                }
                onPress={() => openCycleDocuments(true, "medical_records")}
                showChevron={false}
              />
            </View>

            {showOnIceSection ? (
              <>
                <PastCycleSectionTitle
                  title={t("care.past_cycle_detail.on_ice_from_this_cycle")}
                  icon={Snowflake}
                  action={
                    <ViewAllLink
                      label="View all"
                      onPress={() => navigation.navigate("CryoVault")}
                    />
                  }
                />
                <PastCycleOnIceSection
                  specimens={cycleIceSpecimens}
                  onPressSpecimen={setSelectedSpecimenId}
                  onAddSpecimen={openCycleSpecimenCapture}
                />
              </>
            ) : null}

            <CycleTestsSection
              testResults={cycleTestResults}
              cycleStartDate={apiCycle?.startDate ?? cycle?.startDate ?? null}
              phases={phases}
              cycleLink={{
                label: cycleDocumentFilterLabel,
                cycleId: cycle.id,
                isPast: true,
              }}
              onViewAll={() => navigation.navigate("AllLabResults")}
              onAddTestResult={() =>
                openCycleDocuments(true, "medical_records")
              }
            />

            <CycleProtocolSection
              cycle={pastProtocolData}
              onEditMedication={handleEditMedication}
              onAddMedication={() => setAddMedVisible(true)}
            />

            {/* Monitoring — every monitored cycle type, not IVF alone. A past
                IUI, FET or medicated cycle is followed the same way, so gating
                this on IVF hid records the patient had actually entered. Still
                not unconditional: a cycle type with nothing to monitor should
                not carry the section at all. */}
            {isMonitoredCycle && (
              <OvarianStimulationMonitoringSection
                cycleId={cycle.id}
                cycleType={cycle.cycleType ?? cycle.cycleTypeRaw}
                cycleStartDate={apiCycle?.startDate ?? null}
                phases={phases}
                derivedStepProgress={[]}
                defaultView="chart"
                onUploadReport={(
                  dateISO,
                  onMonitoringExtracted,
                  onDocumentSaved,
                ) => {
                  void pickAndOpenDocumentUpload({
                    defaultCategory: "medical_records",
                    defaultTags: [
                      cycleDocumentFilter,
                      "monitoring-report",
                      `monitoring-date:${dateISO}`,
                    ],
                    onMonitoringExtracted,
                    onSave: onDocumentSaved,
                  });
                }}
              />
            )}

            <CycleRelatedDocumentsSection
              documents={cycleDocuments}
              onViewAll={() => openCycleDocuments(false)}
              onUpload={() => openCycleDocuments(true)}
            />

            <PastCycleSectionTitle
              title="Your team for this cycle"
              icon={HeartHandshake}
            />
            <CycleClinicTeamCard
              members={cycleTeamMembers}
              clinicName={cycleClinicName}
              // TODO(@Bassem, @Olivier): wire these past-cycle care-team
              // actions to backend-backed clinic/provider linking for this
              // cycle, so added items populate useCareTeam(cycle.id).
              onEditTeam={showBackfillToast}
              onAddProviderOrClinic={showBackfillToast}
            />

            {/* Disclaimer */}
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                color: EMBER.colors.textFaint,
                textAlign: "center",
                lineHeight: 18,
                marginTop: 24,
                paddingHorizontal: 20,
              }}
            >
              {
                "Data shown is from your records.\nVerify with your clinic for accuracy."
              }
            </Text>
          </View>
        </ScrollView>
        <UpdatePregnancyModal
          visible={updateOpen}
          onClose={() => {
            setUpdateError(null);
            setUpdateOpen(false);
          }}
          onSave={(status, details) =>
            void handleSavePregnancyStatus(status, details)
          }
          isSaving={recordPregnancyStatus.isPending}
          readReady={!pregnancyStatusUnknown && pregnancyDetailsNote.ready}
          current={currentPregnancyStatus}
          currentDetails={pregnancyDetailsNote.details}
          error={updateError}
        />
      </GlassScreenLayout>
      <CycleExportPreviewDialog
        visible={exportDialogVisible}
        onClose={() => setExportDialogVisible(false)}
      />
      <LabCryoMetricsDialog
        visible={labCryoDialogVisible}
        onClose={() => setLabCryoDialogVisible(false)}
        cycle={cycle}
        onSave={handleSaveLabCryoMetrics}
        saving={labCryoSaving}
        error={labCryoSaveError}
        focusField={labCryoFocusField}
      />
      <AddSpecimenModal
        visible={addSpecimenVisible}
        onClose={() => setAddSpecimenVisible(false)}
        cycleId={cycle.id}
        /**
         * The only way in here is the cycle's own "On ice" section, whose row
         * says "add frozen eggs, embryos or sperm *if this cycle created stored
         * specimens*". That is a cycle-result intake by construction, so the
         * unlinked option must not win: without this she could add a specimen
         * from that section, pick "preservation only", and never see it appear
         * in the section she added it from.
         */
        isCycleResultIntake
      />
      <EditPastCycleDetailsDialog
        visible={editDetailsVisible}
        onClose={() => setEditDetailsVisible(false)}
        cycleId={cycle.id}
        initialClinic={rawCycle?.provider}
        initialStartDate={rawCycle?.startDate}
        initialEndDate={rawCycle?.endDate}
      />
      <AddMedicationModal
        visible={addMedVisible || editMed != null}
        onClose={() => {
          setAddMedVisible(false);
          setEditMed(null);
        }}
        cycleId={cycle.id}
        menstruationStartDate={rawCycle?.menstruationStartDate}
        cycleDateBounds={{
          startDate: apiCycle?.startDate ?? "",
          endDate: apiCycle?.endDate ?? null,
        }}
        editMedication={editMed}
      />
      <CryoSpecimenDetailModal
        specimen={
          cryoSpecimenDetails.find(
            (specimen) => specimen.id === selectedSpecimenId,
          ) ?? null
        }
        allSpecimens={cryoSpecimenDetails}
        visible={selectedSpecimenId != null}
        onClose={() => setSelectedSpecimenId(null)}
        onPlanTreatment={(specimen) => {
          if (
            specimen.type !== "embryo" &&
            specimen.type !== "oocyte" &&
            specimen.type !== "sperm"
          ) {
            return;
          }
          const specimenIds =
            specimen.type === "oocyte" && specimen.oocyteRecordKind === "cohort"
              ? (specimen.cohortMemberSpecimenIds ?? [])
              : [specimen.id];
          if (specimenIds.length === 0) return;
          setSelectedSpecimenId(null);
          openStartTreatment({
            frozenPrefill: {
              type: specimen.type === "sperm" ? "semen" : specimen.type,
              specimenIds,
            },
            source: "PastCycleDetailScreen",
            onCreated: () => navigation.navigate("CycleDetail"),
          });
        }}
        getCycleNavigation={(cycleId) => {
          const navigate = resolveCycleNavigation(cycleId);
          if (!navigate) return undefined;
          return () => {
            setSelectedSpecimenId(null);
            navigate();
          };
        }}
      />
    </>
  );
}

const cardStyle = {
  ...cardSurface(),
  paddingHorizontal: 16,
  paddingVertical: 4,
};
